import * as THREE from 'three';
import {
  BetZone,
  CeeloState,
  CeeloResultType,
  CrapsState,
  SoloState,
  DicePhase,
  DiceThrowParams,
  DiceThrowState,
  DiceSyncPayload,
  FullGameState,
  evaluateCeeloRoll,
  pickCeeloLabel,
  pickCrapsComeOutLabel,
  pickCrapsPointLabel,
  createCrapsRoll,
  evaluateComeOut,
  evaluatePointRoll,
  type CrapsPoint,
  type ComeOutOutcome,
  type PointOutcome,
} from '@sdl/shared';
import type {
  GameStateSnapshot,
  GameTurnStartPayload,
} from '../network/NetworkManager';
import { NetworkManager } from '../network/NetworkManager';
import { renderScheduler } from '../engine/render/RenderScheduler';
import { sound, SoundId } from '../engine/audio/sound';
import { randomShakeSound } from '../engine/audio/SoundCatalog';
import { ceeloLabelKind, ceeloShooterVsBankerKind, crapsComeOutLabelKind, crapsPointLabelKind, type ResultLabelKind } from './dice/resultLabelKind';
import { DiceController } from './dice/DiceController';
import { OpponentDiceManager } from './dice/OpponentDiceManager';
import type { DiceAssetManager } from './dice/assets/DiceAssetManager';
import { CameraController } from './camera/CameraController';
import { BettingController } from './betting/BettingController';
import { BettingHUD3D } from './betting/BettingHUD3D';
import { PlayerProfilesOverlay } from '../ui/components/PlayerProfilesOverlay';
import { RebuyModal } from '../ui/components/RebuyModal';
import { getSidebetIconUrl } from '../ui/components/sidebetIcon';
import type { CountdownOverlay } from '../ui/components/CountdownOverlay';
import { markPhase, clearPhase } from '../util/crashReport';
import { showToast } from '../ui/components/Toast';
import { formatDenomination } from './betting/BillDenominations';
import { SidebetButtonsOverlay } from '../ui/components/SidebetButtonsOverlay';
import { soloStacksCounter } from '../ui/components/SoloStacksCounter';
import { soloRollHistoryOverlay } from '../ui/components/SoloRollHistoryOverlay';
import { SidebetButton } from '../ui/components/SidebetButton';
import type { CancelBetOverlay } from '../ui/components/CancelBetOverlay';
import type { InstructionBanner } from '../ui/components/InstructionBanner';
import { Engine } from '../engine/Engine';
import { gameStateStore } from '../state/GameStateStore';
import type { AnchoredLight } from './scene/GameScene';
import { applyAnchorToLights } from './scene/GameScene';
import type { DiceResultUpdate } from '../ui/components/PlayerProfilesOverlay';
import { CameraAnims, HiLoAnims } from './config/AnimationConfig';
import { worldToScreen } from '../engine/utils/worldToScreen';
import { getNoteCdnUrl } from '../api/currencyNoteApi';
import { TaskPool } from '../engine/core/TaskPool';
import { StateMachine, buildStatesForMode, type SDLGame } from './state-machine/v2';
import type { GameUpdate, SoloRollHistoryEntry } from '@sdl/shared';

export interface GameFlowControllerOptions {
  networkManager: NetworkManager;
  engine?: Engine | null;
  diceController?: DiceController | null;
  cameraController?: CameraController | null;
  bettingController?: BettingController | null;
  bettingHUD3D?: BettingHUD3D | null;
  playerProfilesOverlay?: PlayerProfilesOverlay | null;
  countdownOverlay?: CountdownOverlay | null;
}

export class GameFlowController {
  private networkManager: NetworkManager;
  private diceController: DiceController | null = null;
  private cameraController: CameraController | null = null;
  private bettingController: BettingController | null = null;
  private bettingHUD3D: BettingHUD3D | null = null;
  private cameraAnchors: { shooting: THREE.Object3D; betting: THREE.Object3D } | null = null;
  private playerProfilesOverlay: PlayerProfilesOverlay | null = null;
  private countdownOverlay: CountdownOverlay | null = null;
  private sidebetButtonsOverlay: SidebetButtonsOverlay | null = null;
  private crapsSidebetButton: SidebetButton | null = null;
  private cancelBetOverlay: CancelBetOverlay | null = null;
  private instructionBanner: InstructionBanner | null = null;
  private opponentDiceManager: OpponentDiceManager | null = null;
  private engine: Engine | null = null;
  /** Per-opponent timers for auto-resetting dice after result viewing */
  private opponentResetTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // ── Unified State Update (Phase 3) ──
  /** Cooperative-cancellation epoch. Bumped whenever the TaskPool abandons a
   *  task (watchdog timeout or session reset). Long choreography primitives
   *  (enterBettingMode / exitBettingMode) capture it on entry and compare
   *  after every await: a mismatch means this continuation is a corpse whose
   *  suspended await got released by a later tween kill/drain — it must PARK
   *  (parkForever), not replay stale wallet/camera side effects over the
   *  live state. */
  private flowEpoch = 0;
  private taskPool = new TaskPool({ onAbandon: () => { this.flowEpoch++; } });

  /** A promise that never settles. Returned by choreography primitives when
   *  they detect their task was abandoned: parking the async frame is the
   *  closest JS gets to cancellation. Safe by construction — only corpses
   *  (already abandoned by the TaskPool) ever park, so this can't jam the
   *  queue. */
  private parkForever(): Promise<never> {
    return new Promise<never>(() => {});
  }
  // Generic state machine; per-game handlers are registered on the first
  // GameUpdate via the matching ClientGameStrategy (cee-lo / craps / …).
  /** v2 state machine — drives all per-update lifecycle. */
  private stateMachine: StateMachine | null = null;
  /** Subsystem ref bag shared with every v2 state class. */
  private sdlGame: SDLGame | null = null;
  /** State context built once in bindNetwork; held so late-bound handler
   *  registration (e.g. on state:full first arrival) can access it. */
  private lastUpdateVersion = 0;
  /** gameType the current state map was built for. The map is mode-specific, so
   *  a mode change (e.g. the craps tutorial registering its map on this shared
   *  controller, then a ceelo game) must rebuild it — reset() keeps the old map. */
  private registeredGameType: 'ceelo' | 'craps' | 'solo' | null = null;

  private rollAuthorized = false;
  private bankerId: string | null = null;
  private currentState: string | null = null;
  /** Craps point from update.customState.point — read locally when the
   *  shooter's dice stop so we can synthesise the roll outcome and fire
   *  the dice-result flyout immediately, without waiting for the server's
   *  game:update round trip. */
  private crapsPoint: number | null = null;

  /** Server-derived "is this state a betting state?" — single source of
   *  truth for camera/wallet/light decisions that previously rode on a
   *  parallel client `bettingActive` flag. The flag could go stale on
   *  resume (resetStateMachine cleared the v2 state machine but not the
   *  flag), so enterBettingMode would short-circuit and the wallet
   *  rendered closed after returning from background. The set IS the
   *  contract — when adding a new betting state, add it here. */
  private static readonly BETTING_STATE_IDS: ReadonlySet<string> = new Set<string>([
    CeeloState.BANKER_SET_POT,
    CeeloState.FADING,
    CeeloState.PLAYER_BETTING,
    CrapsState.SHOOTER_SET_STACK,
    CrapsState.FADING,
    CrapsState.SIDEBET_BETTING,
    SoloState.BETTING,
    SoloState.FADE,
  ]);

  private isBettingState(state: string | null | undefined): boolean {
    return !!state && GameFlowController.BETTING_STATE_IDS.has(state);
  }

  /**
   * Can the LOCAL player swap their equipped dice right now?
   *
   * Generous gate (matches the try-screen feel) — allowed whenever their dice
   * are at rest BEFORE dragging, in any phase EXCEPT HI_LO. The player can swap
   * before EVERY throw they make (ceelo round roll, craps come-out + each point
   * roll): the only requirement is they tap the gear before they start dragging
   * the dice up.
   *   • HI_LO is locked — once the roll-off to pick the banker starts, the
   *     contender's single die is committed.
   *   • any betting state (dice not spawned / pre-roll) → ok.
   *   • DiceThrowState.READY (on the felt before pickup, or put back down after
   *     a drag with no throw) → ok.
   *   • DiceThrowState.STOPPED (dice landed from the previous throw and are
   *     resting on the felt waiting for the next pickup — the between-rolls
   *     window) → ok. This is the case that used to be wrongly locked.
   * Everything mid-gesture or in flight (PICKING_UP / HOLDING / THROWING /
   * ROLLING / SETTLING) is locked. Who can even reach this is already limited by
   * gear visibility (showDice), so no separate roller check.
   * Returns a reason so the caller shows the right pill.
   */
  canLocalPlayerSwapDice(): { allowed: boolean; reason: 'ok' | 'hilo' | 'locked' } {
    const localId = this.networkManager?.getPlayerId();
    if (!localId) return { allowed: false, reason: 'locked' };
    if (this.currentState === CeeloState.HI_LO) return { allowed: false, reason: 'hilo' };
    if (this.isBettingState(this.currentState)) return { allowed: true, reason: 'ok' };
    const ds = this.diceController?.state;
    if (!ds || ds === DiceThrowState.READY || ds === DiceThrowState.STOPPED) {
      return { allowed: true, reason: 'ok' };
    }
    return { allowed: false, reason: 'locked' };
  }
  /** Last visible sidebet count — gates camera dolly retweens. Each zone
   * added past ring-1 inner extends the bounds, so retween on every count
   * change to keep the framing tracking the layout. */
  private lastSidebetCount: number | null = null;
  private bankerHasRolled = false;
  private playerCount = 0;
  // ── Re-buy (out-of-bullet) ──
  private rebuyModal: RebuyModal | null = null;
  /** Player opted into silent auto re-buy via the modal checkbox. */
  private autoRebuy = false;
  /** A re-buy is in flight (sent, awaiting the server's balance bump) — gates
   *  re-prompting / re-sending until the stack recovers. */
  private rebuyRequested = false;
  /** Snapshot from the most recent applyPerUpdateBookkeeping. Used by
   *  enterBettingMode to re-sync bet zones AFTER bettingController.start()
   *  flips the controller out of INACTIVE — the initial setBetZones call
   *  earlier in the same update bails when the controller is INACTIVE. */
  private lastSnapshot: GameStateSnapshot | null = null;
  private cdnBase: string | null = null;
  /** Cache equipped dice per player (server doesn't send on every update) */
  private playerEquippedDice = new Map<string, string[]>();
  /** Live dice results from dice:result events (for immediate display before game:state arrives) */
  private liveDiceResults = new Map<string, number[]>();

  /** Last seen `betZones` keyed by id, snapshotted at the END of each
   *  updateBetZones call. Used to detect per-zone transitions (notably
   *  visible:false → visible:true, which triggers
   *  bettingController.handleBetZoneSurfaced). Replaces the legacy
   *  per-event diff in handleBetZoneUpdated. */
  private prevBetZonesById = new Map<string, BetZone>();

  /** Bet zone create-acks that arrived BEFORE the corresponding
   *  game:update.betZones registered the new zone. Drained on the next
   *  updateBetZones call. The ack-then-update ordering can happen
   *  even with the server emitting in the right order, because
   *  Socket.IO doesn't guarantee strict cross-event delivery order. */
  private pendingCreateAcks = new Map<string, string>();

  /** Cee-lo only — captures timestamps along the dice-stopped → onExit
   * pipeline so we can identify which segment is varying when post-roll
   * resolution feels laggy. Cleared after each log emission. */
  private _lagTrace: { stopped?: number; receive?: number; taskStart?: number } = {};

  /** Lights with optional per-anchor overrides from the editor scene. Each
   *  carries `base` — the doc/shooting pose GameScene captured ONCE at load —
   *  so applyAnchorToLights can restore 'shooting' without sampling the live
   *  light (which may be at the betting anchor on a resident-scene handoff). */
  private anchoredLights: AnchoredLight[] = [];

  private diceStoppedHandler = (data: { values: number[] }) => {
    if (!this.rollAuthorized) return;
    this.rollAuthorized = false;
    // In server-authoritative mode, dice:stopped_request already notifies the server.
    // Sending game:roll_result as well causes hiLoRolls pollution on re-rolls:
    // the old-path result arrives AFTER the tie is resolved and hiLoRolls is cleared,
    // making canPlayerRoll() reject the next pickup.
    if (!this.diceController?.getServerAuthoritativeMode()) {
      this.networkManager.sendRollResult(data.values);
    }
    this.diceController?.setEnabled(false);

    // HI_LO: frame all result dice (local + opponents)
    this.updateHiLoCameraFrame();
  };

  // Server-authoritative dice request handlers
  private dicePickupStartHandler = (_data: { timestamp: number }) => {
    this.networkManager.sendDicePickupStart();
  };

  private diceCollectedHandler = (_data: { timestamp: number }) => {
    this.networkManager.sendDiceCollected();
  };

  private diceThrowRequestHandler = (data: {
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>;
    halfExtents?: [number, number, number];
    halfExtentsPerDie?: Array<[number, number, number]>;
    timestamp: number;
  }) => {
    // HI_LO parity dump at throw moment
    if (this.currentState === CeeloState.HI_LO && this.engine) {
      this.engine.physics.dumpBodies('local-throw');
    }
    this.networkManager.sendDiceThrowRequest(data.diceStates, data.halfExtents, data.halfExtentsPerDie);
  };

  // No-throw release: the shooter dropped held dice back to the table. Relay
  // the transforms so spectators replay the same deterministic fall instead of
  // leaving our dice frozen in the air (same relay family as dice:throw).
  private diceDropRequestHandler = (data: {
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>;
    halfExtents?: [number, number, number];
    halfExtentsPerDie?: Array<[number, number, number]>;
    timestamp: number;
  }) => {
    this.networkManager.sendDiceDrop(data.diceStates, data.halfExtents, data.halfExtentsPerDie);
  };

  private diceStoppedRequestHandler = (data: {
    results: number[];
    finalStates: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>;
    timestamp: number;
  }) => {
    this.networkManager.sendDiceStoppedRequest(data.results, data.finalStates);

    // Fire the dice-result flyout LOCALLY the moment the shooter's dice
    // settle, instead of waiting for the server's game:update echo. The
    // shooter is authoritative for "dice stopped"; the server result
    // arrives one network RTT later and was producing a visible delay
    // (icons appear instantly for spectators, who get the flyout from
    // game:update in the same tick as their dice STOPPED phase event).
    //
    // handleNewRollResult dedups on `playerId:dice.join(',')`, so when
    // playRollResolutionPrimitive runs from the server's game:update
    // onExit, the duplicate call is a no-op — only the timing changes.
    const localPlayerId = this.networkManager.getPlayerId();
    if (localPlayerId) {
      const result = this.computeLocalRollResult(data.results);
      this.handleNewRollResult({
        playerId: localPlayerId,
        dice: data.results,
        result,
      });
    }
  };

  /**
   * Synthesize the same `RollResult.result` payload the server would
   * compute, so `handleNewRollResult` picks the correct flyout label.
   * Cee-lo evaluates from dice alone (`evaluateCeeloRoll` already runs
   * inside handleNewRollResult), so we return null for 3-dice rolls.
   * Craps needs the active game state + current point — both are read
   * from the latest game:update snapshot (this.currentState, this.crapsPoint).
   */
  private computeLocalRollResult(
    dice: number[],
  ): unknown | null {
    if (dice.length !== 2) return null;
    const roll = createCrapsRoll(dice[0], dice[1]);
    if (this.currentState === CrapsState.COME_OUT) {
      return { kind: 'come_out', outcome: evaluateComeOut(roll) };
    }
    if (this.currentState === CrapsState.POINT_ROLL && this.crapsPoint != null) {
      const point = this.crapsPoint as CrapsPoint;
      return { kind: 'point_roll', outcome: evaluatePointRoll(roll, point), point };
    }
    return null;
  }

  private diceDragSyncHandler = (data: {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
    seq: number;
    timestamp: number;
  }) => {
    this.networkManager.sendDiceDragSync(data.position, data.rotation, data.seq, data.timestamp);
  };

  // Local shake beat already played its sound in DiceController — this just
  // relays it (with intensity) so opponents hear the rattle too
  // (dice:drag-family relay).
  private diceShakeHandler = (data: { intensity: number }) => {
    this.networkManager.sendDiceShake(data.intensity);
  };

  // Hide the instruction banner while the local player is engaged with the
  // dice (pickup → flight → landed at rest); it returns once the controller
  // is back at READY. Edge-driven off the throw state machine — nothing to
  // poll per frame. The HI_LO case is the poster child: the contender reads
  // "Roll to determine the banker", throws, and the banner stays down while
  // their die is mid-air or resting awaiting the other contenders.
  private diceThrowStateHandler = (data: { state: string }) => {
    this.instructionBanner?.setSuppressed('dice-pickup', data.state !== DiceThrowState.READY);
  };

  constructor(options: GameFlowControllerOptions) {
    this.networkManager = options.networkManager;
    this.setEngine(options.engine ?? null);
    this.setDiceController(options.diceController ?? null);
    this.setCameraController(options.cameraController ?? null);
    this.setBettingController(options.bettingController ?? null);
    this.setBettingHUD3D(options.bettingHUD3D ?? null);
    this.setPlayerProfilesOverlay(options.playerProfilesOverlay ?? null);
    this.setCountdownOverlay(options.countdownOverlay ?? null);

    this.bindNetwork();
    this.applyInitialState();
  }

  setEngine(engine: Engine | null): void {
    this.engine = engine;

    // Dispose old opponent dice manager
    if (this.opponentDiceManager) {
      this.opponentDiceManager.dispose();
      this.opponentDiceManager = null;
    }

    // Create new opponent dice manager if we have an engine
    if (engine) {
      this.opponentDiceManager = new OpponentDiceManager(engine);
      this.opponentDiceManager.setLocalPlayerId(this.networkManager.getPlayerId());
    }
  }

  // Kept for join-time skin-document preloads (game:player_joined) — the
  // OpponentDiceManager holds its own ref for build/reskin.
  private diceAssetManager: DiceAssetManager | null = null;

  setDiceAssetManager(manager: DiceAssetManager | null): void {
    this.diceAssetManager = manager;
    this.opponentDiceManager?.setAssetManager(manager);
  }

  setCdnBase(cdnBase: string | null): void {
    this.cdnBase = cdnBase;
  }

  setDiceController(controller: DiceController | null): void {
    if (this.diceController) {
      this.diceController.off('dice:stopped', this.diceStoppedHandler);
      this.diceController.off('dice:pickup_start_request', this.dicePickupStartHandler);
      this.diceController.off('dice:collected_request', this.diceCollectedHandler);
      this.diceController.off('dice:throw_request', this.diceThrowRequestHandler);
      this.diceController.off('dice:drop_request', this.diceDropRequestHandler);
      this.diceController.off('dice:stopped_request', this.diceStoppedRequestHandler);
      this.diceController.off('dice:drag_sync', this.diceDragSyncHandler);
      this.diceController.off('dice:shake', this.diceShakeHandler);
      this.diceController.off('dice:throw_state', this.diceThrowStateHandler);
    }
    this.diceController = controller;
    if (this.diceController) {
      this.diceController.on('dice:stopped', this.diceStoppedHandler);
      this.diceController.on('dice:pickup_start_request', this.dicePickupStartHandler);
      this.diceController.on('dice:collected_request', this.diceCollectedHandler);
      this.diceController.on('dice:throw_request', this.diceThrowRequestHandler);
      this.diceController.on('dice:drop_request', this.diceDropRequestHandler);
      this.diceController.on('dice:stopped_request', this.diceStoppedRequestHandler);
      this.diceController.on('dice:drag_sync', this.diceDragSyncHandler);
      this.diceController.on('dice:shake', this.diceShakeHandler);
      this.diceController.on('dice:throw_state', this.diceThrowStateHandler);
      // Sync the suppression to the controller's CURRENT state on swap-in —
      // the next edge alone would leave a mid-flight swap stale.
      this.diceThrowStateHandler({ state: this.diceController.state ?? DiceThrowState.READY });
    }
    this.applyInitialState();
  }

  setCameraController(controller: CameraController | null): void {
    this.cameraController = controller;
    this.primeBettingScene();
    this.applyInitialState();
  }

  setBettingController(controller: BettingController | null): void {
    // Remove old listeners
    if (this.bettingController) {
      this.bettingController.off('betting:denomination_changed', this.handleBettingDenominationChanged);
      this.bettingController.off('betting:bill_placed', this.handleBettingBillPlaced);
      this.bettingController.off('betting:bill_returned', this.handleBettingBillReturned);
      this.bettingController.off('betting:activity_changed', this.handleBettingActivityChanged);
      this.bettingController.off('pendingBet:changed', this.handlePendingBetChanged);
      this.bettingController.off('betting:sidebet_type_requested', this.handleSidebetTypeRequested);
    }
    this.bettingController = controller;
    // Add new listeners
    if (this.bettingController) {
      this.bettingController.setLocalPlayerId(this.networkManager.getPlayerId());
      this.bettingController.setCancelBetOverlay(this.cancelBetOverlay);
      this.bettingController.on('betting:denomination_changed', this.handleBettingDenominationChanged);
      this.bettingController.on('betting:bill_placed', this.handleBettingBillPlaced);
      this.bettingController.on('betting:bill_returned', this.handleBettingBillReturned);
      this.bettingController.on('betting:sidebet_type_requested', this.handleSidebetTypeRequested);
      this.bettingController.on('betting:activity_changed', this.handleBettingActivityChanged);
      this.bettingController.on('pendingBet:changed', this.handlePendingBetChanged);
    }
    this.primeBettingScene();
  }

  private handleBettingDenominationChanged = (data: { index: number; value: number }): void => {
    this.bettingHUD3D?.updateDenomination(data.value);
  };

  private handleBettingBillPlaced = (data: { denomination: number; total: number }): void => {
    this.bettingHUD3D?.updateTotal(data.total);
  };

  private handleBettingBillReturned = (_data: { denomination: number }): void => {
    const total = this.bettingController?.getTotalBet() ?? 0;
    this.bettingHUD3D?.updateTotal(total);
  };

  private handleBettingActivityChanged = (data: { active: boolean }): void => {
    this.networkManager.sendBettingActivity(data.active);
  };

  /** Local pulled-but-uncommitted bet amount → preview-deduct it from the local
   *  player's card balance so they see what's left after pulling notes. */
  private handlePendingBetChanged = (data: { amount: number }): void => {
    const localId = this.networkManager.getPlayerId();
    if (localId) this.playerProfilesOverlay?.setLocalPendingBet(localId, data.amount);
  };

  setBettingHUD3D(hud: BettingHUD3D | null): void {
    // Remove old listeners
    if (this.bettingHUD3D) {
      this.bettingHUD3D.off('locked_in', this.handleBettingHUD3DLockedIn);
      this.bettingHUD3D.off('unlocked', this.handleBettingHUD3DUnlocked);
      this.bettingHUD3D.off('sidebet_selected', this.handleBettingHUD3DSidebetSelected);
    }
    this.bettingHUD3D = hud;
    // Add new listeners
    if (this.bettingHUD3D) {
      this.bettingHUD3D.on('locked_in', this.handleBettingHUD3DLockedIn);
      this.bettingHUD3D.on('unlocked', this.handleBettingHUD3DUnlocked);
      this.bettingHUD3D.on('sidebet_selected', this.handleBettingHUD3DSidebetSelected);
    }
    this.primeBettingScene();
  }

  setCameraAnchors(anchors: { shooting: THREE.Object3D; betting: THREE.Object3D } | null): void {
    this.cameraAnchors = anchors;
    this.primeBettingScene();
  }

  /**
   * Push the SDL editor's `betting` camera anchor + derived ground center
   * into the camera, betting controller, and HUD3D. Runs once when all
   * dependencies are ready (called from setCameraAnchors and from each
   * subsystem's setter, idempotent — no-ops until anchors are present).
   *
   * The anchor is scene data, not phase-derived: it doesn't change at
   * runtime. State classes call cameraController.enterBetting() /
   * exitBetting() to lerp between the shooting and betting anchors;
   * this method just primes the destination once.
   */
  private primeBettingScene(): void {
    if (!this.cameraAnchors?.betting) return;
    const anchor = this.cameraAnchors.betting;
    const lookAt = anchor.userData?.lookAt as number[] | undefined;
    if (!lookAt) return;

    this.cameraController?.setBettingAnchor(
      { x: anchor.position.x, y: anchor.position.y, z: anchor.position.z },
      { x: lookAt[0], y: lookAt[1], z: lookAt[2] },
      anchor.userData?.fov
    );
    const groundCenter = this.computeBettingZoneCenter(anchor, lookAt);
    this.bettingController?.setBettingCenter(groundCenter);
    this.bettingHUD3D?.setGroundCenter(groundCenter);
  }

  /**
   * Set lights with per-anchor overrides from the editor scene.
   * Captures the base (shooting) state of each light so it can be restored
   * when transitioning back to the shooting phase.
   */
  setAnchoredLights(lights: AnchoredLight[]): void {
    this.anchoredLights = lights;

    // Apply the initial 'shooting' anchor — lights start in their base
    // (shooting) state. base comes straight from AnchoredLight.base (the
    // doc/shooting pose GameScene captured ONCE at load), never sampled from the
    // live light: with the scene resident (SceneManager) the light may be sitting
    // at the betting anchor when this runs (tutorial → game handoff, or leaving a
    // match mid-betting), and sampling it would bake the betting pose in as
    // "shooting". Transitions are driven by enterBettingMode / exitBettingMode.
    this.applyLightAnchor('shooting');
  }

  /**
   * Snap all anchored lights to the given phase's resolved state.
   *
   * This USED to GSAP-crossfade and restore each light's visibility/intensity
   * inside the tween's onComplete. But applyLightAnchor kills in-flight tweens at
   * the start of the next transition, so a fade interrupted mid-flight never ran
   * its restore — across rounds that left fill lights stuck dim or hidden, the
   * scene progressively lost fill, and the dice rendered half-black on their unlit
   * faces. Snapping is idempotent: every call fully resets each light from base
   * (+ betting override), so nothing can drift no matter how transitions interleave.
   * The ~0.8s camera tween masks the instant light change.
   */
  private applyLightAnchor(anchorName: 'shooting' | 'betting'): void {
    // Single source of truth lives in GameScene (applyAnchorToLights) so the
    // resident-scene try-dice flows reset 'shooting' light identically. base
    // comes from each AnchoredLight (the doc/shooting pose GameScene captured
    // once) — same object stored in this.baseLightStates.
    applyAnchorToLights(this.anchoredLights, anchorName);
  }

  private handleBettingHUD3DLockedIn = (): void => {
    // Clear any uncommitted accumulated bills before locking in
    if ((this.bettingController?.getPendingBetAmount() ?? 0) > 0) {
      this.bettingController!.clearAccumulatedBills();
    }

    const totalBet = this.bettingController?.getTotalBet() ?? 0;
    // Sidebet phases allow lock-in with zero bets ("I pass on sidebets,
    // ready to roll"). Cee-lo's PLAYER_BETTING and craps's SIDEBET_BETTING
    // share this semantic — faders / sidebetters can opt out of placing
    // and still signal ready. FADING (both games) requires a bet because
    // the whole point is covering the banker / shooter's stack.
    const allowZeroBet = this.currentState === CeeloState.PLAYER_BETTING
      || this.currentState === CrapsState.SIDEBET_BETTING;
    if (totalBet <= 0 && !allowZeroBet) {
      this.bettingHUD3D?.setLockedIn(false);
      return;
    }

    // Animate wallet closed, then tell server we're ready
    this.bettingController?.closeWallet(() => {
      this.networkManager.sendReady();
    });
  };

  private handleBettingHUD3DUnlocked = (): void => {
    // Tell server player is no longer ready
    this.networkManager.sendUnready();
    // Re-open wallet if still in side betting phase (just open, no entry animation)
    if (this.currentState === CeeloState.PLAYER_BETTING
        || this.currentState === CrapsState.SIDEBET_BETTING) {
      this.bettingController?.openWallet();
    }
  };

  private handleSidebetTypeRequested = (data: { requestId: string; amount: number }): void => {
    // Bills are now in the create zone (wallet pending zeroed) but the server
    // won't deduct until a sidebet type is picked — hold the preview deduction
    // so the balance doesn't blink back up while the type selector is open.
    const localId = this.networkManager.getPlayerId();
    if (localId) this.playerProfilesOverlay?.commitLocalPendingBet(localId, data.amount);

    const availableLabels = this.resolveSidebetLabels();

    // Show DOM sidebet buttons overlay (preferred) or fall back to 3D HUD
    if (this.sidebetButtonsOverlay) {
      this.sidebetButtonsOverlay.setAvailableOptions(availableLabels);
      // Position anchor at the create zone (sidebet drop target)
      if (this.bettingController) {
        const createPos = this.bettingController.getCreateZonePosition();
        this.sidebetButtonsOverlay.setAnchorPosition(createPos);
      }
      this.sidebetButtonsOverlay.show();
    } else if (this.bettingHUD3D) {
      this.bettingHUD3D.setSidebetOptions(availableLabels);
      this.bettingHUD3D.setSidebetOptionsVisible(true);
    }
  };

  /**
   * Pick the sidebet labels to display for the current betting window.
   *
   * SERVER-AUTHORITATIVE: the server sends `availableSidebets` (canonical bet
   * codes) for the current window — cee-lo keys off bankerHasRolled, craps off
   * whether a point is set. We just map those codes to the overlay's display
   * labels. No more client-side window inference (which previously guessed
   * SW/SL during a mid-point window from a stale `comeOutRoll`).
   *
   * Falls back to the old per-game inference only if the server didn't send
   * the field (older server / very early state).
   */
  /**
   * Labels primed by primeSidebetOptions from the authoritative state-machine
   * update (the game:update the betting state entered on). Preferred source
   * for the sidebet selector — see primeSidebetOptions / resolveSidebetLabels.
   */
  private currentSidebetLabels: string[] | null = null;

  /**
   * Resolve the sidebet selector options for the CURRENT betting window from
   * the authoritative `update.availableSidebets` and cache them. Called from
   * the betting states' onEnter/onUpdate (SidebetBettingState / PlayerBettingState).
   *
   * This is the fix for "SW/SL shown after a point is set": the selector used
   * to resolve lazily on bill-drop from `getLastGameState()` (the legacy
   * game:state side-channel), which could be stale/divergent from the
   * game:update the state machine actually transitioned on. Now the window's
   * options are locked in at entry from the same update the state machine saw.
   */
  primeSidebetOptions(update: GameUpdate): void {
    const codes = update.availableSidebets;
    const labels = Array.isArray(codes)
      ? codes
          .map((c) => GameFlowController.SIDEBET_CODE_TO_LABEL[c])
          .filter((l): l is string => !!l)
      : [];
    this.currentSidebetLabels = labels.length > 0 ? labels : null;
  }

  private resolveSidebetLabels(): string[] {
    // Preferred: options primed from the authoritative state-machine update.
    if (this.currentSidebetLabels && this.currentSidebetLabels.length > 0) {
      return this.currentSidebetLabels;
    }

    // ── Fallback (no primed value yet — older server / pre-enter race) ──
    const lastState = this.networkManager.getLastGameState();
    const codes = lastState?.availableSidebets;
    if (Array.isArray(codes) && codes.length > 0) {
      return codes
        .map((c) => GameFlowController.SIDEBET_CODE_TO_LABEL[c])
        .filter((l): l is string => !!l);
    }
    const gameType = lastState?.gameType;
    if (gameType === 'craps') {
      const customState = (lastState?.customState as { point?: unknown }) ?? {};
      return customState.point != null
        ? ['4/10', '5/9', '6/8']
        : ['SW', 'SL'];
    }
    return this.bankerHasRolled
      ? ['BANK WINS', 'BANK LOSES', '4+', '3-']
      : ['4+', '3-'];
  }

  /**
   * Canonical server bet-code → overlay display-label map. Inverse of the
   * betTypeMap in main.ts (label → code), kept in lockstep with it and with
   * SIDEBET_OPTIONS in SidebetButtonsOverlay.ts. Codes are the shared server
   * enum strings (CeeloSideBetType / CrapsSideBetType).
   */
  private static readonly SIDEBET_CODE_TO_LABEL: Record<string, string> = {
    // Cee-lo
    bank_wins: 'BANK WINS',
    bank_loses: 'BANK LOSES',
    four_up: '4+',
    three_down: '3-',
    // Craps — pre-come-out turn-level
    shooter_wins: 'SW',
    shooter_loses: 'SL',
    // Craps — mid-point point pairs
    point_pair_4_10: '4/10',
    point_pair_5_9: '5/9',
    point_pair_6_8: '6/8',
    // Solo — Hi-Lo
    lo: 'LO',
    seven: '7',
    hi: 'HI',
    // Solo — Chuck-a-Luck faces
    face_1: '1',
    face_2: '2',
    face_3: '3',
    face_4: '4',
    face_5: '5',
    face_6: '6',
  };

  private handleSidebetButtonSelected(label: string): void {
    if (!this.bettingController) return;
    this.sidebetButtonsOverlay?.hide();
    this.bettingHUD3D?.setSidebetOptionsVisible(false);
    this.bettingController.confirmCreateSideBet(label);

    // (Legacy "quick sidebet during roll" auto-exit removed — in v2,
    // sidebets are picked from the dedicated betting states only, and
    // the rolling state's onEnter owns the exitBettingMode call.)
  }

  private handleBettingHUD3DSidebetSelected = (data: { label: string }): void => {
    if (!this.bettingController) return;
    this.bettingHUD3D?.setSidebetOptionsVisible(false);
    this.sidebetButtonsOverlay?.hide();
    this.bettingController.confirmCreateSideBet(data.label);
  };

  setPlayerProfilesOverlay(overlay: PlayerProfilesOverlay | null): void {
    this.playerProfilesOverlay = overlay;
  }

  setCountdownOverlay(overlay: CountdownOverlay | null): void {
    this.countdownOverlay = overlay;
  }

  /**
   * Resolves once the state machine has processed its first game update
   * (usually state:full on join). Main uses this to keep the loading screen
   * up until the current state's onEnter has rendered its UI, so no player
   * sees an empty scene before the countdown / profiles / wallet appear.
   */
  getFirstUpdateProcessed(): Promise<void> {
    return this.stateMachine?.firstUpdateProcessed ?? Promise.resolve();
  }

  /**
   * Reset the state machine for a new game session. Clears any leftover
   * queued updates, resets the machine's currentState, and rebuilds the
   * firstUpdateProcessed barrier so the loading screen actually awaits the
   * new game's first onEnter (not an already-resolved promise from the
   * previous game). Call this before reconnecting to the game server.
   */
  resetStateMachine(): void {
    // Hard reset, not clear(): reset() also abandons the IN-FLIGHT task. If a
    // wedged animation await ever jams the pool (the cee-lo stuck-in-betting
    // incident), this is what lets the resume state:full snap past the corpse
    // instead of queueing behind it forever.
    this.taskPool.reset();
    this.stateMachine?.reset();
    // Clear the game:update dedup high-water mark. This is the new-game-session
    // boundary, so prior-session versions must not carry over — most notably the
    // tutorial, which drives this same controller and (historically) stamped its
    // scripted updates with Date.now(). Leaving that ~1.7e12 here made every real
    // game's small stateVersion look "stale" and get dropped, so a player who ran
    // the tutorial then joined a game never transitioned past the join snapshot.
    this.lastUpdateVersion = 0;
    // Per-player equipped-dice entries would otherwise accumulate one array
    // per distinct playerId for the whole session (GFC is a singleton). This
    // is the new-game boundary; the next game's updates repopulate it.
    this.playerEquippedDice.clear();
  }

  setCancelBetOverlay(overlay: CancelBetOverlay | null): void {
    if (this.cancelBetOverlay && this.cancelBetOverlay !== overlay) {
      this.cancelBetOverlay.setOnCancel(null);
    }
    this.cancelBetOverlay = overlay;
    overlay?.setOnCancel(() => this.handleCancelBetClicked());
    this.bettingController?.setCancelBetOverlay(overlay);
  }

  setInstructionBanner(banner: InstructionBanner | null): void {
    this.instructionBanner = banner;
  }

  private handleCancelBetClicked(): void {
    const controller = this.bettingController;
    if (!controller?.hasPendingCreate()) return;
    // The sidebet type selector (B / B / 4+ / 3-) is showing because of
    // this pending create. Hide it before discarding the bills so the
    // player isn't left staring at options for a bet they just cancelled.
    this.sidebetButtonsOverlay?.hide();
    this.bettingHUD3D?.setSidebetOptionsVisible(false);
    controller.cancelCreateSideBet();
    // Bills returned to the wallet (which re-grows localPendingBet); drop the
    // in-flight commit deduction so it isn't double-counted.
    const localId = this.networkManager.getPlayerId();
    if (localId) this.playerProfilesOverlay?.releaseLocalInFlight(localId);
  }

  setSidebetButtonsOverlay(overlay: SidebetButtonsOverlay | null): void {
    this.sidebetButtonsOverlay = overlay;
    if (overlay) {
      overlay.setOnSelect((label: string) => {
        this.handleSidebetButtonSelected(label);
      });
    }
  }

  dispose(): void {
    window.removeEventListener('beforeunload', this._appExitHandler);
    window.removeEventListener('pagehide', this._appExitHandler);
    if (this.diceController) {
      this.diceController.off('dice:stopped', this.diceStoppedHandler);
      this.diceController.off('dice:pickup_start_request', this.dicePickupStartHandler);
      this.diceController.off('dice:collected_request', this.diceCollectedHandler);
      this.diceController.off('dice:throw_request', this.diceThrowRequestHandler);
      this.diceController.off('dice:drop_request', this.diceDropRequestHandler);
      this.diceController.off('dice:stopped_request', this.diceStoppedRequestHandler);
      this.diceController.off('dice:drag_sync', this.diceDragSyncHandler);
    }
    if (this.bettingController) {
      this.bettingController.off('betting:denomination_changed', this.handleBettingDenominationChanged);
      this.bettingController.off('betting:bill_placed', this.handleBettingBillPlaced);
      this.bettingController.off('betting:bill_returned', this.handleBettingBillReturned);
      this.bettingController.off('betting:activity_changed', this.handleBettingActivityChanged);
      this.bettingController.off('pendingBet:changed', this.handlePendingBetChanged);
    }
    if (this.bettingHUD3D) {
      this.bettingHUD3D.off('locked_in', this.handleBettingHUD3DLockedIn);
      this.bettingHUD3D.off('unlocked', this.handleBettingHUD3DUnlocked);
    }
    this.rebuyModal?.destroy();
    this.rebuyModal = null;
    // Clear all opponent reset timers
    for (const timer of this.opponentResetTimers.values()) clearTimeout(timer);
    this.opponentResetTimers.clear();
    if (this.opponentDiceManager) {
      this.opponentDiceManager.dispose();
      this.opponentDiceManager = null;
    }
    this.countdownOverlay?.stop();
    this.taskPool.reset();
    this.diceController = null;
    this.cameraController = null;
    this.bettingController = null;
    this.bettingHUD3D = null;
    this.playerProfilesOverlay = null;
    this.anchoredLights = [];
  }

  /**
   * Real app-unload / tab-close while in a game → deliberate room:leave so the
   * server clean-removes us (settle + drop) instead of a lingering inactive
   * seat. Bound once, removed in dispose() (no leak). Same leaveRoom() path
   * will back the menu's "exit game" button.
   *
   * CRITICAL: `pagehide` with `persisted === true` is bfcache / mobile
   * BACKGROUNDING (the page may be restored) — NOT a real exit. We must NOT
   * leave on that, or an app-switch on native would clean-leave the player.
   * Backgrounding is the keep-seat/reconnect case, handled separately by the
   * visibilitychange → sendActiveStatus(false) path in main.ts. Native kills
   * fire no JS event at all → server disconnect→inactive is the fallback.
   *
   * Best-effort: a hard close may not flush the emit before the socket closes.
   */
  private readonly _appExitHandler = (e: Event): void => {
    // `persisted ?? true`: if a platform ever omits the flag, err toward
    // backgrounding (keep the seat). A false leave costs the player their
    // game; a kept seat is cleaned up by the server's inactivity kick.
    if (e.type === 'pagehide' && ((e as PageTransitionEvent).persisted ?? true)) return;
    if (this.networkManager.isInGame()) this.networkManager.leaveRoom();
  };

  private bindNetwork(): void {
    window.addEventListener('beforeunload', this._appExitHandler);
    // pagehide(persisted=false) is the more reliable real-unload signal on
    // mobile/Safari where beforeunload is flaky; the persisted guard above
    // keeps backgrounding from being treated as a leave.
    window.addEventListener('pagehide', this._appExitHandler);
    this.networkManager.on('game:state', (data) => this.handleGameState(data));
    this.networkManager.on('game:player_joined', (data) => {
      if (data.equippedDice?.length) {
        this.playerEquippedDice.set(data.playerId, data.equippedDice);
        // Start the skin-document download the MOMENT we learn the player
        // exists — join always precedes their dice ever being shown, so by
        // the first activation (ready-dice / pickup / throw) the doc is
        // cached and the activation-point ensureSlotSkins applies it. This
        // is what guarantees nobody ever sees the fallback red box. Dedup'd
        // inside preload; best-effort (the per-update fallback poll retries).
        void this.diceAssetManager?.preload(data.equippedDice).catch(() => { /* poll retries */ });
      }
    });
    this.networkManager.on('game:turn_start', (data) => this.handleTurnStart(data));
    // Roll authorization is STATE (update.rollAuthorizedFor), consumed in each
    // roll state's onEnter/onUpdate — no game:roll_authorized / game:roll_cancelled
    // event path (those were transient unicasts lost on background→resume).
    this.networkManager.on('game:disconnected', () => {
      this.playerProfilesOverlay?.hide();
      this.bettingController?.stop();
      this.bettingHUD3D?.hide();
      this.countdownOverlay?.stop();
    });

    // Bet zone state (created / updated / closed / queued-surface) flows
    // exclusively through game:update.betZones — applyPerUpdateBookkeeping
    // → updateBetZones reconciles. The only targeted bet-zone events
    // that remain carry creator/fader-specific request-id state for
    // local bill animations.
    this.networkManager.on('game:bet_zone_create_ack', (data) => this.handleBetZoneCreateAck(data));
    this.networkManager.on('game:bet_zone_fade_result', (data) => this.handleBetZoneFadeResult(data));
    this.networkManager.on('game:bet_zone_create_rejected', (data) => this.handleBetZoneCreateRejected(data));

    // Dice drag sync from other players
    this.networkManager.on('game:dice_sync', (data) => this.handleDiceSync(data));
    this.networkManager.on('dice:shake', (data) => this.handleOpponentShake(data));

    // Server-authoritative events
    this.networkManager.on('state:full', (data) => this.handleStateFull(data));
    this.networkManager.on('dice:phase', (data) => this.handleDicePhase(data));
    // dice:authorize removed — local roll setup is owned by roll states via
    // setupLocalRoll (gated on update.rollAuthorizedFor), resume-safe state.
    // dice:result removed — results now arrive via game:update.lastRollResult
    this.networkManager.on('reconnect:token', (data) => this.handleReconnectToken(data));

    // Config sync (shooting params updated from editor)
    this.networkManager.on('config:shooting', (data) => this.handleShootingConfigUpdate(data));

    // Payout events (bet/sidebet wins)
    this.networkManager.on('game:payout', (data) => this.handlePayout(data));

    // Roll wins — queued during roll states, processed in post-roll sequence
    this.networkManager.on('game:roll_wins', (data) => this.handleRollWins(data));

    // (Craps sidebet resolutions no longer use a separate craps:sidebet_resolved
    // event — the win now rides game:update.rollWins so the state machine plays
    // the flyout in choreographed sequence, after the dice flyout.)

    // ── v2 state machine wire-up ──
    // SDLGame is the ref bag every state class sees; the StateMachine
    // routes per-update lifecycle hooks. Mode-specific state map gets
    // registered lazily on first GameUpdate once gameType is known.
    this.sdlGame = this.buildSDLGame();
    this.stateMachine = new StateMachine();

    // Bottom-center sidebet pre-arm button; only visible during craps and
    // only in windows where the locked design allows pre-arming.
    this.crapsSidebetButton = new SidebetButton({
      onPress: () => this.networkManager.sendSidebetButtonPress(),
    });

    this.networkManager.on('game:update', (data) => {
      // Dedup by version (monotonically increasing on server, never repeats).
      if (data.version <= this.lastUpdateVersion) return;
      this.lastUpdateVersion = data.version;

      // A server update almost always mutates the 3D world (new phase, dice,
      // bet zones, flyouts). Pulse the renderer so the change draws at full
      // rate immediately instead of waiting for the idle heartbeat — most of
      // what follows is gsap/physics and self-sustains from here.
      renderScheduler.pulse();

      // Cee-lo lag-trace: capture when this update hit the client.
      if (data.gameType === 'ceelo' && this._lagTrace.stopped !== undefined) {
        this._lagTrace.receive = performance.now();
      }

      this.ensureStatesRegistered(data.gameType);

      // Sync the craps sidebet button before dispatching to the state
      // machine. Reading customState from each update keeps the visible
      // status in lockstep with the server's authoritative pre-arm set.
      this.updateCrapsSidebetButton(data);

      // Queue through TaskPool for sequential processing. Cross-cutting
      // per-update bookkeeping (bet zones, opponent dice, sidebet listings,
      // wallet input gate, profile-card timer ring) runs FIRST so the
      // state's onEnter/onUpdate sees a synced world. Per-state UI setup
      // (camera, wallet open/close, instruction banner) is the final word.
      //
      // After the state's onEnter/onUpdate fully resolves we emit the
      // `player:ready_ack` for this update's version. Server uses the
      // ACK to release its `_waitPlayers` gate that defers the per-phase
      // timer until clients have processed the broadcast (camera lerp,
      // dice spawn, wallet slide). Without this ACK the gate always
      // hits its 8s timeout — which is the "long pause" we were seeing
      // after every join / phase / continue-roll. Roll states await
      // dice-authorize before resolving, so the ACK lands at the
      // "dice are pickup-ready" moment per the comment trail (and
      // matches `getDiceCountForCurrentState`'s expectation).
      this.taskPool.enqueue(async () => {
        // Cee-lo lag-trace: capture when this task actually started
        // running (vs queued). High delta from receive→taskStart means
        // the previous task was still draining (e.g., in-flight flyout).
        if (data.gameType === 'ceelo' && this._lagTrace.stopped !== undefined) {
          this._lagTrace.taskStart = performance.now();
        }
        const epoch = this.flowEpoch;
        this.applyPerUpdateBookkeeping(data);
        await this.stateMachine!.process(data);
        // Abandoned mid-process and revived later — don't ack a dead update
        // into a new session (server version-gates acks anyway; belt+braces).
        if (epoch !== this.flowEpoch) return;
        this.networkManager.sendPlayerReady(data.version);
      }, `update v${data.version} → ${data.gameState}`);
    });
  }

  /**
   * Lazily register the mode-specific state map on the first game-state
   * event. Called from both `game:update` and `state:full` listeners —
   * whichever arrives first picks the gameType and builds the state map.
   *
   * Cee-lo's WAITING state for a fresh join arrives only as `state:full`
   * (no transition → no game:update), so registering only on game:update
   * caused the WAITING UI to never render.
   */
  private ensureStatesRegistered(gameType: string): void {
    if (!this.stateMachine || !this.sdlGame) return;
    if (gameType !== 'ceelo' && gameType !== 'craps' && gameType !== 'solo') {
      console.warn('[GameFlowController] unknown gameType, defaulting to ceelo:', gameType);
      gameType = 'ceelo';
    }
    const mode = gameType as 'ceelo' | 'craps' | 'solo';
    // Rebuild when the mode changes, NOT just when no map exists. reset() keeps
    // the previously built map registered (same-mode re-join needs no rebuild),
    // but the map is mode-specific: the craps tutorial registers its map on this
    // shared controller, then a ceelo game starts. Without rebuilding, ceelo-only
    // states (BANKER_SET_POT, …) have no handler and their onEnter — the camera /
    // wallet / light betting setup — silently never runs.
    if (this.stateMachine.hasStates() && this.registeredGameType === mode) return;
    const states = buildStatesForMode(mode, this.stateMachine, this.sdlGame);
    this.stateMachine.registerStates(states);
    this.registeredGameType = mode;
  }

  /**
   * Drive the craps sidebet button from server-authoritative state.
   *
   * Availability is derived from `gameState` + `customState.point` — both of
   * which the server actually puts on the wire (point rides
   * CrapsStrategy.getGameUpdateExtras.customState). We deliberately do NOT
   * read a `sidebetButtonAvailable` flag: the engine's customState map is
   * clobbered by the strategy's getGameUpdateExtras customState object before
   * broadcast, so any setCustomState flag never reaches the client.
   *
   * Driving off the broadcast state instead means this is correct on the
   * mid-game-join / reconnect path too — handleStateFull calls this with the
   * synthetic update, so a player landing mid-point sees the button on load.
   *
   * Visible ONLY when actionable: once a point is set and we're in the
   * point-roll flow (POINT_ESTABLISHED → POINT_ROLL → POINT_RESULT). Every
   * other state — including the SIDEBET_BETTING window itself (bet is picked
   * inside the window, the trigger isn't needed) — is fully hidden
   * (display:none), never a greyed/transparent box.
   */
  private updateCrapsSidebetButton(update: GameUpdate): void {
    if (!this.crapsSidebetButton) return;
    if (update.gameType !== 'craps') {
      this.crapsSidebetButton.setStatus('hidden');
      return;
    }
    const cs = (update.customState ?? {}) as {
      point?: number | null;
      sidebetPreArmed?: string[];
    };
    const localId = this.networkManager.getPlayerId();
    const preArmed = !!localId && Array.isArray(cs.sidebetPreArmed) && cs.sidebetPreArmed.includes(localId);
    const state = update.gameState;

    const inPointPhase =
      (state === CrapsState.POINT_ESTABLISHED
        || state === CrapsState.POINT_ROLL
        || state === CrapsState.POINT_RESULT)
      && cs.point != null;
    if (inPointPhase) {
      this.crapsSidebetButton.setStatus(preArmed ? 'pre-armed' : 'available');
      return;
    }

    this.crapsSidebetButton.setStatus('hidden');
  }

  /**
   * Build the SDLGame ref bag passed to every v2 state class. Subsystem
   * refs are exposed via getters so they're pulled live at access time
   * (not snapshotted at construction) — this keeps state classes
   * working through swap-ins via setDiceController / setCameraController
   * / setPlayerProfilesOverlay / etc.
   *
   * Composite methods (enterBettingMode, exitBettingMode,
   * playRollResolution, runShooterHandoff, handleNewRollResult)
   * delegate to existing private GameFlowController methods to keep
   * the cutover focused. They can be inlined / pushed onto subsystems
   * incrementally as a follow-up.
   */
  private buildSDLGame(): SDLGame {
    const self = this;
    return {
      get engine() { return self.engine; },
      get networkManager() { return self.networkManager; },
      get diceController() { return self.diceController; },
      get opponentDiceManager() { return self.opponentDiceManager; },
      get cameraController() { return self.cameraController; },
      get bettingController() { return self.bettingController; },
      get bettingHUD3D() { return self.bettingHUD3D; },
      get playerProfilesOverlay() { return self.playerProfilesOverlay; },
      get countdownOverlay() { return self.countdownOverlay; },
      get instructionBanner() { return self.instructionBanner; },
      get crapsSidebetButton() { return self.crapsSidebetButton; },
      get cdnBase() { return self.cdnBase; },

      getLocalPlayerId: () => self.networkManager.getPlayerId(),
      delay: (ms) => self.delay(ms),
      waitForAllFlyouts: () =>
        self.playerProfilesOverlay?.waitForAllFlyouts() ?? Promise.resolve(),
      setupLocalRoll: (state) => self.setupLocalRoll(state),
      getDiceCenterScreen: (playerId) => self.getDiceCenterScreen(playerId) ?? null,

      enterBettingMode: (opts) => self.enterBettingMode(opts),
      exitBettingMode: (opts) => self.exitBettingMode(opts),
      ensureShootingView: () => self.ensureShootingView(),

      playRollResolution: (update) => self.playRollResolutionPrimitive(update),
      runShooterHandoff: (update, prevId, newId) =>
        self.runShooterHandoffPrimitive(update, prevId, newId),
      handleNewRollResult: (result) => self.handleNewRollResult(result),
      resultNeedsReroll: (result) => {
        // Cee-lo "no count" → shooter must re-roll. Mirrors shared needsReroll
        // (result.type === NOTHING) with a null/shape guard so a same-state
        // roll update can decide whether to re-arm the local shooter's pickup.
        const r = result as { type?: CeeloResultType } | null;
        return !!r && r.type === CeeloResultType.NOTHING;
      },

      hasProcessedResult: (playerId, dice) =>
        self.processedRollResults.has(`${playerId}:${dice.join(',')}`),
      markResultProcessed: (playerId, dice) => {
        self.processedRollResults.add(`${playerId}:${dice.join(',')}`);
      },
      clearResultDedup: () => self.processedRollResults.clear(),
      clearRoundResults: (animated) => {
        self.liveDiceResults.clear();
        self.processedRollResults.clear();
        self.playerProfilesOverlay?.clearAllDiceResults(animated);
      },
      primeBetZones: () => self.primeBetZones(),
      primeSidebetOptions: (update) => self.primeSidebetOptions(update),
    };
  }

  /**
   * Eager spawn of betting zones — see SDLGame.primeBetZones docstring.
   * Pulled out as its own method so the rolling-state onExit can call it
   * after post-roll choreography finishes, AND enterBettingMode can call
   * the same body without duplicating the start+update sequence.
   */
  private primeBetZones(): void {
    if (!this.bettingController) return;
    // Gate: only prime when the destination state actually needs zones.
    // currentState is set from update.gameState by applyPerUpdateBookkeeping
    // BEFORE onExit fires, so by the time a rolling state calls this from
    // its onExit, currentState is already the NEW state. Skipping the prime
    // for non-betting destinations avoids two failure modes:
    //   1. onIdleEnter fires wallet.show() during a still-rolling frame.
    //   2. Intermediate transitions (e.g. cee-lo PLAYER_ROLL → ROUND_END
    //      → STARTING → HI_LO) tear the primed zone down before
    //      BANKER_SET_POT ever sees it.
    if (!this.isBettingState(this.currentState)) return;
    this.bettingController.start();
    if (this.lastSnapshot) {
      this.updateBetZones(this.lastSnapshot, { syncStacks: true });
    }
  }

  // ── CeeloStateContext implementation methods ──

  /** Apply state data from a unified GameUpdate (delegates to existing applyGameState) */
  /** Convert GameUpdate to GameStateSnapshot for existing applyGameState */
  private gameUpdateToSnapshot(update: GameUpdate): GameStateSnapshot {
    return {
      roomId: update.roomId,
      gameType: update.gameType as 'ceelo' | 'craps',
      state: update.gameState,
      pot: update.pot,
      bankerId: update.bankerId,
      currentPlayerId: update.currentPlayerId,
      roundNumber: update.roundNumber,
      timeRemaining: update.timeRemaining,
      players: update.players?.map(p => ({
        playerId: p.playerId,
        username: p.username,
        balance: p.balance,
        bet: p.bet,
        isReady: p.isReady,
        equippedDice: p.equippedDice,
        result: p.result as any,
        isBetting: (p as any).isBetting,
        avatar: (p as any).avatar,
      })),
      betZones: update.betZones,
      bankerResult: update.lastRollResult?.result as any ?? undefined,
      bankerHasRolled: update.bankerHasRolled,
      hiLoRounds: update.hiLoRounds,
      hiLoContenders: update.hiLoContenders,
      hiLoRolls: update.hiLoRolls,
      shooterQueue: (update as any).shooterQueue,
      currentShooterIndex: (update as any).currentShooterIndex,
    };
  }

  /**
   * Sync the profile-card timer ring(s) to the server's authoritative
   * `update.timer`. The ring shows on the *active* player(s) for the
   * current state — banker for set-pot/fade, the upcoming/current shooter
   * for sidebet/roll, every contender for HI_LO.
   */
  private applyTurnTimer(update: GameUpdate): void {
    if (!this.playerProfilesOverlay) return;
    const activeIds = this.resolveActiveTimerPlayers(update);
    this.playerProfilesOverlay.setProfileTimer(activeIds, update.timer);
  }

  /**
   * Suppress the instruction banner while the local player is mid-
   * betting in BANKER_SET_POT / SHOOTER_SET_STACK / FADING — they're
   * already acting, the prompt is redundant noise. Sidebet phases
   * (PLAYER_BETTING / SIDEBET_BETTING) keep the banner up as a
   * title-only phase header (the option-listing subtitle was removed —
   * it overlapped the sidebet bills once 3+ zones were placed).
   *
   * Driven by `update.players[].isBetting` (server flips on wallet
   * accumulator change). The legacy v1 path used to do this inside
   * `updateProfiles`; v2 promotes it into the cross-cutting
   * bookkeeping pass.
   */
  private applyBettingActivityBannerSuppression(update: GameUpdate): void {
    const localId = this.networkManager.getPlayerId();
    if (!localId) return;
    const localIsBetting = (update.players ?? []).some(
      (p) => p.playerId === localId && (p as { isBetting?: boolean }).isBetting,
    );
    const isSidebetPhase =
      update.gameState === CeeloState.PLAYER_BETTING
      || update.gameState === CrapsState.SIDEBET_BETTING;
    this.instructionBanner?.setSuppressed('betting-activity', localIsBetting && !isSidebetPhase);
  }

  /**
   * Which players should show the countdown RING this update — a pure
   * derivation of the on-the-clock player(s) from broadcast state. The
   * server owns ALL timer behavior: it decides when the timer is visible
   * (silent pre-timer vs counting), and it resets the pre-timer when a
   * player acts — so an actively-betting/rolling player simply rides a
   * silent (visible:false) timer and this never paints a ring on them.
   * The client adds no activity heuristics of its own.
   *
   * Craps shooter id rides BOTH currentPlayerId and bankerId (aliased to
   * ctx.shooterId in CrapsStrategy.getGameUpdateExtras).
   */
  private resolveActiveTimerPlayers(update: GameUpdate): string[] {
    if (!update.timer) return [];
    switch (update.gameState) {
      case CeeloState.HI_LO:
      case CrapsState.HI_LO: {
        // Every contender that hasn't rolled yet is on the clock.
        const rolled = new Set(Object.keys(update.hiLoRolls ?? {}));
        return (update.hiLoContenders ?? []).filter((id) => !rolled.has(id));
      }
      case CeeloState.BANKER_SET_POT:
      case CeeloState.BANKER_ROLL:
        // Banker is on the clock (placing pot / rolling).
        return update.bankerId ? [update.bankerId] : [];
      case CeeloState.PLAYER_BETTING:
      case CeeloState.PLAYER_ROLL:
        // currentPlayerId is the upcoming/current shooter for these states.
        return update.currentPlayerId ? [update.currentPlayerId] : [];
      case CrapsState.SHOOTER_SET_STACK:
      case CrapsState.COME_OUT:
      case CrapsState.POINT_ROLL: {
        // The shooter is on the clock (funding their stack / rolling).
        const shooter = update.currentPlayerId ?? update.bankerId;
        return shooter ? [shooter] : [];
      }
      case CeeloState.FADING:
      case CrapsState.FADING: {
        // Faders are on the clock — every non-shooter player.
        const shooter = update.gameState === CeeloState.FADING
          ? update.bankerId
          : (update.currentPlayerId ?? update.bankerId);
        if (!shooter) return [];
        return (update.players ?? [])
          .map((p) => p.playerId)
          .filter((id) => id !== shooter);
      }
      case CrapsState.SIDEBET_BETTING:
        // Open sidebet window — everyone is on the clock (the shooter also
        // places their own B/S sidebets during this window).
        return (update.players ?? []).map((p) => p.playerId);

      default:
        return [];
    }
  }

  private lastUpdateAnimated = true;

  /**
   * Composite intent: bring the camera to the betting anchor and the wallet
   * into its target visibility. Used by all three betting state classes
   * (BANKER_SET_POT, FADING, PLAYER_BETTING) in their onEnter via
   * ctx.enterBettingMode. Camera step runs first; wallet only animates once
   * the camera has fully arrived (so the wallet's screenToWorld projections
   * are accurate by the time it's positioning itself).
   *
   * Idempotent across both legs — if the camera is already at the betting
   * anchor and the wallet is already at the requested target, both await
   * calls resolve immediately and the whole method is a single tick.
   *
   * Late-join (animated:false): both legs snap and resolve immediately.
   */
  private async enterBettingMode(opts: {
    open: boolean;
    animated: boolean;
    previousState: string | null;
  }): Promise<void> {
    // Cooperative cancellation: if this choreography's task gets abandoned
    // (TaskPool watchdog / session reset) while we're suspended on an await,
    // a later tween-release must NOT let us resume and replay a stale wallet
    // slide-in over the new owner's state. Park instead (see parkForever).
    const epoch = this.flowEpoch;
    // Server is the source of truth for "where we just came from" via
    // update.previousState. enteringFromNonBetting derived directly —
    // no parallel client flag to drift out of sync on resume.
    const enteringFromNonBetting = !this.isBettingState(opts.previousState);
    markPhase('betting:enter');

    // Hide the previous state's instruction banner BEFORE the camera /
    // wallet transition starts. Otherwise the leftover label (e.g.
    // "Hi-Lo / Roll to determine the banker") rides through the lerp
    // and the new banner only swaps in at the very end. The state
    // class shows the new banner AFTER awaiting enterBettingMode, so
    // the visible sequence is now: hide → transition → show.
    this.instructionBanner?.hide();

    // Clear shooting-state dice BEFORE the camera lerp starts. The
    // earlier "clear after transition" version meant the dice were
    // visible mid-lerp from the betting angle and then popped out the
    // moment the camera arrived — that final pop was ugly. Clearing
    // first means the pop happens in the rolling-view frame, where the
    // user's eye is already following the camera moving away.
    // Skipped on betting → betting transitions (set-pot → fade →
    // sidebet) — there are no shooting dice to clear.
    if (enteringFromNonBetting) {
      this.opponentDiceManager?.hideAllResultLabels();
      this.diceController?.clearDice();
      this.cameraController?.setDice([]);
      this.opponentDiceManager?.resetAllOpponents();
      // Invariant: dice input is live ONLY during roll states. Coming from a
      // roll state (incl. an interrupted shooter), the controller may still be
      // enabled — disable it so betting-table pointer gestures (bill drags
      // into slots) can't be hijacked by dice pickup logic / camera follow.
      // Re-enabled by setupLocalRoll when a roll state is next entered.
      this.diceController?.setEnabled(false);
    }

    // Spawn the bet zones FIRST — before the camera lerp starts — so
    // the shooter-stack zone (banker) and any locally-created sidebet
    // zone are visible in the rolling-view frame and ride through the
    // lerp into the betting view. Otherwise the zones would only appear
    // at the start of the lerp (after bettingController.start() flips
    // the state out of INACTIVE), giving a brief beat of empty table
    // before the zone pops in.
    //
    // Everything in this block is synchronous; the camera lerp + wallet
    // slide-in still run async below.
    if (this.bettingController) {
      this.bettingHUD3D?.setLockedIn(false);
      // Idempotent: if the rolling-state onExit already called
      // primeBetZones(), start() + updateBetZones are no-ops here.
      this.primeBetZones();
      if (!enteringFromNonBetting) {
        this.bettingController.syncWalletTransformImmediate();
      }
      const denom = this.bettingController.getCurrentDenomination();
      const balance = this.bettingController.getBalance();
      const total = this.bettingController.getTotalBet();
      this.bettingHUD3D?.updateDenomination(denom);
      this.bettingHUD3D?.updateBalance(balance);
      this.bettingHUD3D?.updateTotal(total);
      this.bettingHUD3D?.show();
      if (enteringFromNonBetting) {
        this.bettingController.hide();
      }
    }

    markPhase('betting:zones-primed');

    // Lights go to the betting anchor UNCONDITIONALLY — not gated on
    // enteringFromNonBetting. update.previousState says where the SERVER came
    // from, not what this client's scene currently shows: a late joiner's
    // FIRST processed update can be the join broadcast itself (e.g. craps
    // SIDEBET_BETTING with previousState=FADING — addPlayer's sendUpdate
    // reaches the joiner before any state:full), so gating on previousState
    // left the lights at the shooting baseline while camera + wallet entered
    // betting (the dark-table late-join bug). applyLightAnchor snaps
    // idempotently from base, so re-applying on betting → betting sub-state
    // hops is visually free. Fired before the camera await so lights and
    // camera still move together on animated entries.
    if (this.anchoredLights.length > 0) {
      this.applyLightAnchor('betting');
    }
    const cameraPromise = this.cameraController
      ? this.cameraController.enterBetting({ animated: opts.animated })
      : Promise.resolve();

    // Wait for camera to settle. Now is the first moment screenToWorld
    // projections at the betting anchor are accurate, so refresh the
    // wallet input gate BEFORE the wallet animates in.
    await cameraPromise;
    if (epoch !== this.flowEpoch) return this.parkForever();
    // The camera await can also be released by an out-of-band setMode (e.g.
    // a dice event yanked the camera to 'follow', killing the betting lerp).
    // The betting anchor was never reached, so screenToWorld projections are
    // garbage — showing the wallet now would place it at a wrong pose. Bail;
    // the next update re-enters betting idempotently if it's still due.
    if (this.cameraController && this.cameraController.getMode() !== 'betting') {
      clearPhase();
      return;
    }
    markPhase('betting:camera-done');
    this.updateBettingInputGate();
    if (this.bettingController) {
      await this.bettingController.showWallet({ open: opts.open, animated: opts.animated });
      if (epoch !== this.flowEpoch) return this.parkForever();
    }
    clearPhase(); // betting entry completed without crashing
  }

  /**
   * Composite inverse: hide the wallet, return the camera to the default
   * (shooting) anchor, tear down betting subsystems, fade lights back to
   * the shooting anchor. Wallet hides first so it's not still visible
   * while the camera is panning away from the betting position.
   *
   * Idempotent: no-op when not in betting mode.
   */
  private async exitBettingMode(opts: {
    animated: boolean;
    previousState: string | null;
  }): Promise<void> {
    // Cooperative cancellation — same contract as enterBettingMode.
    const epoch = this.flowEpoch;
    // No-op when the state we're exiting FROM wasn't a betting state.
    // Replaces the legacy bettingActive flag — server's previousState
    // is the single source of truth for "were we in betting?".
    //
    // RESUME EXCEPTION: the state:full synthetic update carries
    // previousState: null, so a client that backgrounded during betting and
    // resumed after the server moved to a roll state would early-return here
    // and keep its wallet open + betting camera for the whole roll phase —
    // "stuck in betting mode" without any hang. The camera's own mode is the
    // observable truth for "betting visuals are up": tear down whenever
    // either signal says betting. (Both paths stay idempotent.)
    const clientVisiblyInBetting = this.cameraController?.getMode() === 'betting';
    if (!this.isBettingState(opts.previousState) && !clientVisiblyInBetting) return;

    // Same banner-hide pattern as enterBettingMode: clear the leftover
    // label as soon as the transition starts; the next state owns
    // showing its own banner (or staying empty).
    this.instructionBanner?.hide();

    // Wallet hides first (symmetric inverse of enterBettingMode, where the
    // wallet shows AFTER the camera) so it isn't still on-screen while the
    // camera pans away.
    if (this.bettingController) {
      await this.bettingController.hideWallet({ animated: opts.animated });
      if (epoch !== this.flowEpoch) return this.parkForever();
    }

    // Lights + camera move TOGETHER: fire the light crossfade the instant the
    // camera lerp starts so they translate in parallel (same 0.8s / ease,
    // settle together). Previously the lights fired before the awaited wallet
    // hide, so they led the camera by the wallet-hide duration — the lights
    // visibly moved first. (Mirrors enterBettingMode's parallel kick-off.)
    if (this.anchoredLights.length > 0) {
      this.applyLightAnchor('shooting');
    }
    if (this.cameraController) {
      await this.cameraController.exitBetting({ animated: opts.animated });
      if (epoch !== this.flowEpoch) return this.parkForever();
    }

    this.bettingController?.clearAllBettingObjects();
    this.bettingController?.stop();
    this.bettingHUD3D?.reset();
    this.bettingHUD3D?.setSidebetOptionsVisible(false);
    this.sidebetButtonsOverlay?.hide();
  }

  /**
   * Idempotent "ensure SHOOTING baseline" — see SDLGame.ensureShootingView.
   * Lights → shooting anchor + full betting-subsystem teardown, regardless of
   * where we came from, WITHOUT moving the camera. The non-roll transitional
   * states call this on entry so a server jump straight from a betting phase to
   * WAITING/STARTING/ROUND_END/GAME_END (mid-round leave abandon) can't leave
   * the lights on the betting anchor or the betting controller eating input.
   */
  private ensureShootingView(): void {
    // Lights snap from base every call (applyLightAnchor is idempotent +
    // instant), so this is safe whether or not we were in betting.
    if (this.anchoredLights.length > 0) {
      this.applyLightAnchor('shooting');
    }
    // Betting subsystem teardown. Every call is idempotent: hide()/stop()/
    // reset() on an already-torn-down controller/HUD are no-ops, so this is a
    // free no-op on the normal between-rounds path and a real teardown on the
    // abandon-from-betting path.
    this.instructionBanner?.hide();
    this.bettingController?.clearAllBettingObjects();
    this.bettingController?.stop();
    this.bettingController?.hide(); // snap wallet off-screen (non-animated)
    this.bettingHUD3D?.reset();
    this.bettingHUD3D?.setSidebetOptionsVisible(false);
    this.sidebetButtonsOverlay?.hide();
  }

  /** Play dice equipped flyout for shooter announcement */
  private async playShooterAnnouncement(playerId: string): Promise<void> {
    if (!this.playerProfilesOverlay || !this.cdnBase) return;
    const equippedDice = this.playerProfilesOverlay.getEquippedDiceFor(playerId)
      ?? this.playerEquippedDice.get(playerId);
    if (!equippedDice?.length) return;
    await this.playerProfilesOverlay.flyoutDiceAnnouncement(playerId, equippedDice, this.cdnBase);
  }

  private handleShootingConfigUpdate(data: { config: any; version: number }): void {
    if (this.diceController) {
      this.diceController.updateConfig(data.config);
    }
  }

  private handleStateFull(data: FullGameState): void {
    gameStateStore.applyFullState(data);

    // Apply opponent phases from server state (for reconnection)
    // This ensures newly connected players see correct dice visibility
    if (data.playerRollStates && Object.keys(data.playerRollStates).length > 0) {
      this.opponentDiceManager?.applyPlayerRollStates(data.playerRollStates);
    }

    // If there are dice in motion, restore their state
    if (data.rollState?.phase === DicePhase.THROWN && data.rollState.throwParams) {
      // Apply the throw to local dice controller
      this.diceController?.applyServerThrow(data.rollState.throwParams);
    }

    // Also apply throws from playerRollStates (for multi-roller phases like HI_LO)
    if (data.playerRollStates) {
      const localPlayerId = this.networkManager.getPlayerId();
      for (const [playerId, rollState] of Object.entries(data.playerRollStates)) {
        // THROWN = dice mid-flight, replay the throw. IDLE + throwParams = the
        // shooter dropped held dice without throwing (the server keeps the drop
        // snapshot: every die at rest, zero velocity) — replay it as an instant
        // settle so a late joiner / resumer sees the dice on the floor where the
        // shooter left them, not an empty table.
        const replayableDrop = rollState.phase === DicePhase.IDLE && rollState.throwParams;
        if (playerId !== localPlayerId && (rollState.phase === DicePhase.THROWN || replayableDrop) && rollState.throwParams) {
          this.ensureOpponentLoaded(playerId);
          this.loadShooter(playerId);
          this.opponentDiceManager?.handleThrow(playerId, rollState.throwParams as DiceThrowParams);
        }

        if (playerId !== localPlayerId && (rollState as any).dragPose) {
          const dragPose = (rollState as any).dragPose as {
            position: { x: number; y: number; z: number };
            rotation: { x: number; y: number; z: number; w: number };
            seq: number;
            timestamp: number;
          };
          const diceCount = (rollState as any).diceCount ?? this.getDiceCountForCurrentState();
          this.opponentDiceManager?.hydrateDragSnapshot(playerId, dragPose, diceCount);
        }
      }
    }

    if (data.betZones && data.betZones.length > 0) {
      this.applyBetZones(data.betZones, data.players);
    }

    // Feed the initial state into the state machine so WAITING/STARTING get
    // proper onEnter (practice dice, countdown, etc). Build a synthetic
    // GameUpdate from the FullGameState — non-animated since this is a join/reconnect.
    if (this.stateMachine) {
      // Register the state map if not already done. For a fresh cee-lo
      // room the FIRST state event is `state:full`, NOT `game:update` —
      // so the game:update listener's lazy-register doesn't fire for
      // joins. Without this, process() bails on "no states" and the
      // WAITING UI never renders.
      this.ensureStatesRegistered(data.gameType);

      const syntheticUpdate: GameUpdate = {
        ...data,
        timestamp: Date.now(),
        animated: false,
        previousState: null,
        lastRollResult: null,
        rollWins: [],
        turnAction: null,
        tieResult: null,
        forfeit: null,
        timer: null,
      };
      // Sync the sidebet button on the join/reconnect path too — a player
      // landing mid-point must see the right button state on load, not wait
      // for the next game:update (the game:update listener is the only OTHER
      // caller of this).
      this.updateCrapsSidebetButton(syntheticUpdate);
      // Resume bookkeeping: keep currentState/bankerId/crapsPoint/lastSnapshot
      // fresh immediately so dice:phase / dice:result handlers that may arrive
      // before the next game:update don't read stale values (the next
      // game:update would otherwise be the first thing to set them).
      this.applyUpdateScalars(syntheticUpdate);
      this.lastSnapshot = this.gameUpdateToSnapshot(syntheticUpdate);
      this.taskPool.enqueue(
        () => this.stateMachine!.process(syntheticUpdate),
        `state:full → ${syntheticUpdate.gameState}`
      );
    }
  }

  private handleDicePhase(data: {
    playerId: string;
    phase: DicePhase;
    diceCount?: number;
    throwParams?: DiceThrowParams;
    reconciliation?: Array<{
      playerId: string;
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
      asleep?: boolean;
    }>;
    finalStates?: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>;
  }): void {
    gameStateStore.handleDicePhase(data);

    // Opponent phase changes (throw / drop / stopped) move dice — render the
    // transition at full rate; physics/gsap take over from here.
    renderScheduler.pulse();

    const localPlayerId = this.networkManager.getPlayerId();
    const shouldSpectatorFollow = this.currentState !== CeeloState.HI_LO;

    // If this is for another player, manage opponent dice
    if (data.playerId !== localPlayerId) {
      // Get dice count from server (authoritative) or fall back to throw params or local state
      let diceCount: number;
      if (data.diceCount !== undefined) {
        // Server-authoritative dice count
        diceCount = data.diceCount;
      } else if (data.throwParams) {
        // Infer from throw params
        diceCount = data.throwParams.diceStates.length;
      } else {
        // Fallback to local state (less reliable)
        diceCount = this.getDiceCountForCurrentState();
      }

      // Ensure opponent has correct dice count BEFORE setting phase
      this.opponentDiceManager?.ensureOpponentDiceCount(data.playerId, diceCount);

      // Cancel pending reset timer if opponent starts a new action (re-roll after no score)
      if (data.phase === DicePhase.COLLECTING || data.phase === DicePhase.THROWN) {
        this.clearOpponentResetTimer(data.playerId);
      }

      // ALWAYS update opponent's phase from server (server-authoritative)
      // This controls when opponent dice become visible in handleDiceSync
      this.opponentDiceManager?.setOpponentPhase(data.playerId, data.phase);

      // Handle throw - apply physics to opponent's dice (now with correct count)
      if (data.phase === DicePhase.THROWN && data.throwParams) {
        // HI_LO reconciliation: snap existing dice to server-authoritative state
        // before adding the new die, so all clients start from identical state
        if (data.reconciliation?.length) {
          this.applyHiLoReconciliation(data.reconciliation);
        }
        // Build the opponent's dice from their equipped asset BEFORE replaying
        // the throw. A brand-new joiner's dice:phase(THROWN) can beat the
        // game:update that would have loaded them with equippedDice; without
        // this, handleThrow auto-loads an info-less red cube whose collider
        // diverges from the shooter's real die for the whole roll.
        this.ensureOpponentLoaded(data.playerId);
        // Single-shooter world (Phone Dice loadPlayer): park every other die so
        // this replay can't collide with the previous roller's stray bodies —
        // the root of "second roller needs a correction snap". Skipped in HI_LO.
        this.loadShooter(data.playerId);
        this.opponentDiceManager?.handleThrow(data.playerId, data.throwParams);
      }
      // Handle no-throw drop — the shooter released held dice without throwing.
      // A drop is a zero-velocity "throw": route it through the same path so the
      // drag group is torn down and the dice fall to the table deterministically
      // instead of hanging frozen in the air. NOT a roll — no result, no camera
      // follow, and the server has already reset the phase to IDLE.
      else if (data.phase === DicePhase.DROPPED && data.throwParams) {
        this.ensureOpponentLoaded(data.playerId);
        this.loadShooter(data.playerId);
        this.opponentDiceManager?.handleThrow(data.playerId, data.throwParams);
        // Keep the identity pill over the now-resting dice (whose dice are
        // down, about to roll again) — mirrors the shooter's PICK UP DICE.
        this.opponentDiceManager?.showDropIdentityLabel(
          data.playerId,
          data.throwParams.diceStates.map((s) => s.position),
        );
      }
      // Handle dice stopped
      else if (data.phase === DicePhase.STOPPED) {
        // Dice cleanup is owned by the state machine (ctx.clearDice() in
        // RollStateHandler.onExit / HiLoStateHandler.onExit). No auto-reset
        // timer here — the old 3s timer was a legacy path that raced the
        // state machine and cleared dice before flyout sequences finished.

        // Full-world re-alignment first: the settle beat carries a snapshot of
        // EVERY die in the server world (the last throw of a round has no
        // subsequent THROWN broadcast, so this is what corrects knocked
        // settled dice). Aligned dice epsilon-skip, so this is a no-op when
        // the sims agree.
        if (data.reconciliation?.length) {
          this.applyHiLoReconciliation(data.reconciliation);
        }

        // Correct any local divergence (edge-stuck die, settle drift) by
        // snapping to the shooter's authoritative post-nudge pose. During
        // HI_LO the settled die stays a LIVE sleeping collider (knockable,
        // falls with gravity — matched by the server world's settleDie);
        // other phases park it (single-shooter clean-world model).
        if (data.finalStates?.length) {
          this.opponentDiceManager?.applyFinalStates(data.playerId, data.finalStates, {
            park: !this.isHiLoState(),
          });
        }

        // HI_LO: show name label above opponent's die and frame all result dice
        if (this.currentState === CeeloState.HI_LO) {
          this.opponentDiceManager?.showResultLabel(data.playerId);
        }
        this.updateHiLoCameraFrame();
      }

      if (shouldSpectatorFollow && this.cameraController && this.opponentDiceManager) {
        const opponentDice = this.opponentDiceManager.getOpponentActiveDice(data.playerId);
        if (opponentDice.length > 0) {
          if (data.phase === DicePhase.COLLECTING || data.phase === DicePhase.HOLDING) {
            this.cameraController.setMode('default');
          } else if (data.phase === DicePhase.THROWN) {
            this.cameraController.setDice(opponentDice);
            this.cameraController.setMode('follow');
          }
          // STOPPED: don't auto-zoom here — it ran ahead of the dice-result
          // flyout, producing a visible "zoom-then-flyout" beat. The zoom
          // is now triggered by handleNewRollResult (which fires from the
          // game:update lastRollResult in the same tick as the flyout) so
          // the camera move and the icon fly-out start together. Pickup
          // remains instantly available (setEnabled is independent of the
          // camera mode).
        }
      }
    } else {
      // Local thrower's own echo. There are two modes:
      //
      // 1. Physics-deferred (HI_LO): the local throw captured params but
      //    did NOT apply physics — dice stayed frozen. The server echo IS
      //    the trigger. applyServerThrow starts the simulation here on all
      //    clients identically (determinism for simultaneous rolling).
      //
      // 2. Local-authoritative (non-HI_LO): the local throw already fired
      //    physics on this client. The server echo arrives ~RTT later.
      //    Calling applyServerThrow at that point snaps the dice back to
      //    throw-start (where they were RTT ago) and re-applies velocity,
      //    producing a visible "teleport + double launch" on prod-RTT
      //    connections. The local physics is authoritative for the single
      //    thrower — skip the replay.
      //
      // Spectators take a different branch (opponentDiceManager.handleThrow)
      // and are unaffected by either mode.
      if (data.phase === DicePhase.THROWN && data.throwParams) {
        if (data.reconciliation?.length) {
          this.applyHiLoReconciliation(data.reconciliation);
        }
        if (this.diceController?.isPhysicsDeferred()) {
          this.diceController.applyServerThrow(data.throwParams);
        }
      }
      // Our own stop echo carries the full-world snapshot too — apply it so
      // opponents' knocked dice re-align on OUR screen as well. Our own die's
      // entry is asleep at the finalStates pose we just reported → epsilon
      // no-op.
      else if (data.phase === DicePhase.STOPPED && data.reconciliation?.length) {
        this.applyHiLoReconciliation(data.reconciliation);
      }
    }
  }

  /**
   * Get the expected dice count for the current game state
   */
  private getDiceCountForCurrentState(): number {
    return this.getDiceCountForState(this.currentState);
  }

  /** Expected dice count for a given state (param form — resume-safe, no
   *  reliance on this.currentState which isn't set on the state:full path). */
  private getDiceCountForState(state: string | null): number {
    // HI_LO phase uses 1 die
    if (state === CeeloState.HI_LO) {
      return 1;
    }
    // Craps uses 2 dice
    if (state === CrapsState.COME_OUT ||
        state === CrapsState.POINT_ROLL) {
      return 2;
    }
    // Solo: count depends on the selected sub-mode (Hi-Lo 2, Chuck-a-Luck 3),
    // which lives on customState.soloMode — the state string alone can't tell.
    if (state === SoloState.ROLL) {
      const mode = (this.networkManager.getLastGameState()?.customState as { soloMode?: string } | undefined)?.soloMode;
      return mode === 'chuckaluck' ? 3 : 2;
    }
    // Cee-Lo and default uses 3 dice
    return 3;
  }

  /** Project the center of a player's dice to screen coordinates for flyout animation */
  private getDiceCenterScreen(playerId: string): { x: number; y: number } | undefined {
    if (!this.engine) return undefined;
    const localId = this.networkManager.getPlayerId();
    const dice = playerId === localId
      ? (this.diceController?.getDice() ?? [])
      : (this.opponentDiceManager?.getOpponentActiveDice(playerId) ?? []);
    if (dice.length === 0) return undefined;

    // Compute center of all dice mesh positions
    let cx = 0, cy = 0, cz = 0;
    for (const die of dice) {
      const p = die.mesh.position;
      cx += p.x; cy += p.y; cz += p.z;
    }
    cx /= dice.length; cy /= dice.length; cz /= dice.length;

    return worldToScreen({ x: cx, y: cy, z: cz }, this.engine.camera, this.engine.canvas) ?? undefined;
  }

  /**
   * Get dice starting positions based on count
   */
  private getDicePositions(count: number): Array<{ x: number; y: number; z: number }> {
    switch (count) {
      case 1:
        // Single die (HI_LO)
        return [{ x: -0.8, y: 1, z: 5 }];
      case 2:
        // Two dice (Craps)
        return [
          { x: -0.75, y: 1, z: 5 },
          { x: 0.75, y: 1, z: 5 },
        ];
      case 3:
      default:
        // Three dice (Cee-Lo)
        return [
          { x: -1.5, y: 1, z: 5 },
          { x: 0, y: 1, z: 5 },
          { x: 1.5, y: 1, z: 5 },
        ];
    }
  }

  /**
   * During HI_LO, update the camera to frame all dice that have results
   * (local stopped dice + all opponent stopped dice).
   */
  private updateHiLoCameraFrame(): void {
    if (this.currentState !== CeeloState.HI_LO) return;
    if (!this.cameraController || !this.opponentDiceManager) return;

    // Only frame all results once the local player's own die has stopped.
    // Any other state (READY, PICKING_UP, HOLDING, THROWING, ROLLING, SETTLING)
    // means the player is still interacting — don't move the camera.
    if (this.diceController?.state !== DiceThrowState.STOPPED) return;

    // Collect all dice with results: local stopped + all opponent stopped
    const allResultDice: import('./dice/Dice').Dice[] = [
      ...this.diceController.getDice(),
      ...this.opponentDiceManager.getAllStoppedDice(),
    ];

    if (allResultDice.length > 0) {
      this.cameraController.setDice(allResultDice);
      this.cameraController.setMode('zoom');
    }
  }

  /** Track which playerIds we've already processed a result for (to dedup state machine calls) */
  private processedRollResults = new Set<string>();

  /**
   * Process a new dice result from game:update.lastRollResult.
   * Called by the state machine when an update contains a result.
   * Returns true if this is a new result (not a duplicate of last seen).
   */
  private handleNewRollResult(rollResult: { playerId: string; dice: number[]; result: unknown | null }): boolean {
    const dedupKey = `${rollResult.playerId}:${rollResult.dice.join(',')}`;
    if (this.processedRollResults.has(dedupKey)) return false;
    this.processedRollResults.add(dedupKey);

    gameStateStore.handleDiceResult({ playerId: rollResult.playerId, results: rollResult.dice });

    // Derive the showdown label from the result. Returns null for outcomes
    // that should NOT show on the profile / fire a flyout (cee-lo NOTHING
    // re-rolls, craps continue point-rolls).
    //
    // Dispatch on the game MODE, not the dice count: solo Chuck-a-Luck also
    // throws 3 dice, and running it through the cee-lo evaluator showed a
    // bogus "POINT n" on pair rolls (and would swallow the flyout entirely on
    // cee-lo-NOTHING combos). Solo shows no outcome label at all; its win /
    // loss beat rides the payout flyout from settlement.
    let label: string | null = null;
    let labelKind: ResultLabelKind | null = null;
    let isShowable = true;
    const gameType = this.lastSnapshot?.gameType;
    if (gameType === 'ceelo' && rollResult.dice.length === 3) {
      const ceeloResult = evaluateCeeloRoll(rollResult.dice as [number, number, number]);
      if (ceeloResult.type === CeeloResultType.NOTHING) {
        this.liveDiceResults.delete(rollResult.playerId);
        return true;
      }
      label = pickCeeloLabel(ceeloResult);
      labelKind = ceeloLabelKind(ceeloResult);
      // Shooter rolls are relative to the banker: rolling under the banker's
      // rank is a loss even on a scoring point (and over it, a win). The
      // banker's scoring dice are cached in liveDiceResults from their roll.
      if (this.bankerId && rollResult.playerId !== this.bankerId) {
        const bankerDice = this.liveDiceResults.get(this.bankerId);
        if (bankerDice?.length === 3) {
          const bankerResult = evaluateCeeloRoll(bankerDice as [number, number, number]);
          if (bankerResult.rank > 0) {
            labelKind = ceeloShooterVsBankerKind(ceeloResult, bankerResult);
          }
        }
      }
    } else if (gameType === 'craps' && rollResult.dice.length === 2) {
      // Craps: server-provided CrapsRollOutcome rides on rollResult.result.
      const r = rollResult.result as
        | { kind: 'come_out'; outcome: ComeOutOutcome }
        | { kind: 'point_roll'; outcome: PointOutcome; point: CrapsPoint }
        | { kind: 'unknown' }
        | null;
      if (r?.kind === 'come_out') {
        label = pickCrapsComeOutLabel(r.outcome);
        labelKind = crapsComeOutLabelKind(r.outcome);
      } else if (r?.kind === 'point_roll') {
        if (r.outcome.kind === 'continue') {
          // Continue rolls don't show on the profile or fire a flyout.
          isShowable = false;
        } else {
          label = pickCrapsPointLabel(r.outcome, r.point);
          labelKind = crapsPointLabelKind(r.outcome);
        }
      }
    }

    if (!isShowable) return true;

    // Sort dice into display order BEFORE caching and flyout so every consumer
    // (live cache → profile rebuild, flyout animation) shows the canonical
    // arrangement: 1-2-3 / 4-5-6 ascending, pair then odd point, trips natural.
    // Cee-lo only — solo chuck-a-luck keeps roll order so this flyout matches
    // the profile-card path (State.sortDiceForDisplay passes non-cee-lo through).
    const displayDice = gameType === 'ceelo'
      ? GameFlowController.sortCeeloDiceForDisplay(rollResult.dice)
      : [...rollResult.dice];

    // Cache live result for scoring rolls only
    this.liveDiceResults.set(rollResult.playerId, displayDice);

    // Play dice result flyout immediately (label rides in the same call)
    this.playDiceResultFlyout(rollResult.playerId, displayDice, true, label, labelKind);

    // Zoom the camera onto the rolling player's dice the instant they settle.
    // The local shooter zooms on their OWN dice too — this is what makes the
    // try-dice / tutorial result feel slick (their `dice:stopped → setMode('zoom')`),
    // and the in-game shooter was previously the only one left without it.
    // Riding handleNewRollResult means the punch-in fires immediately on the
    // local-fire path and is a no-op on the deduped server echo, so the move
    // lands the moment the dice come to rest — same `setMode('zoom')` tween for
    // shooter and spectators. HI_LO is excluded: it frames all dice together
    // via updateHiLoCameraFrame below.
    const localPlayerId = this.networkManager.getPlayerId();
    if (this.currentState !== CeeloState.HI_LO && this.cameraController) {
      const resultDice = rollResult.playerId === localPlayerId
        ? (this.diceController?.getDice() ?? [])
        : (this.opponentDiceManager?.getOpponentActiveDice(rollResult.playerId) ?? []);
      if (resultDice.length > 0) {
        this.cameraController.setDice(resultDice);
        this.cameraController.setMode('zoom');
      }
    }

    // HI_LO: frame all result dice as each player's result lands
    this.updateHiLoCameraFrame();
    return true;
  }

  private handleReconnectToken(data: { token: string }): void {
    gameStateStore.setReconnectionToken(data.token);
  }

  private handleDiceSync(data: DiceSyncPayload): void {
    this.opponentDiceManager?.handleDiceSync(data);

    if (this.currentState === CeeloState.HI_LO || !this.cameraController || !this.opponentDiceManager) {
      return;
    }
  }

  /** Relayed shake beat from another player — quieter than the local rattle
   *  so someone else's shaking reads as background table noise, scaled by
   *  how hard they're actually shaking. */
  private handleOpponentShake(data: { playerId: string; intensity?: number }): void {
    if (data.playerId === this.networkManager.getPlayerId()) return;
    const intensity = Math.min(1, Math.max(0, data.intensity ?? 1));
    sound.play(randomShakeSound(), { volume: 0.6 * (0.45 + 0.55 * intensity) });
  }

  private applyInitialState(): void {
    // No-op — state initialization is now handled by the state machine via
    // state:full → handleStateFull → synthetic GameUpdate → TaskPool.
    // Controllers are guaranteed to exist because the game server connection
    // is deferred until after scene setup (UDG pattern).
  }

  private handleTurnStart(_data: GameTurnStartPayload): void {
    // No-op — dice clearing and shooter announcements are now handled
    // by the v2 state machine via game:update → state.onEnter / onExit.
  }

  /**
   * Establish the LOCAL player's roll setup: server-authoritative mode, dice
   * spawn (or HI_LO reroll-keep), camera framing, controller enable.
   *
   * Roll authorization is pure STATE: roll states call this from onEnter (and
   * HI_LO/POINT_ROLL onUpdate for reroll/continue) when the local id is in
   * `update.rollAuthorizedFor`. There is NO `game:roll_authorized` event path
   * anymore — that made authorization a transient unicast lost on resume.
   *
   * Idempotent — the dice-count guard skips re-creation and the flag/mode
   * setters are no-ops on repeat. Takes `state` explicitly (NOT
   * this.currentState) so it's correct on the state:full resume path too,
   * where currentState isn't set by applyPerUpdateBookkeeping.
   */
  setupLocalRoll(state: string): void {
    this.rollAuthorized = true;
    const expectedDiceCount = this.getDiceCountForState(state);
    const isHiLo = state === CeeloState.HI_LO;

    // HI_LO parity dump: snapshot the local physics world (scene + dice) so we
    // can diff against the server's [PhysicsDump:SERVER:*] output.
    if (isHiLo && this.engine) {
      this.engine.physics.dumpBodies('hi_lo-authorized');
    }

    // Clear opponent dice before local player's re-roll (prevents stale dice from previous shooter)
    // During HI_LO, keep opponent dice — they need to stay visible for result framing
    if (!isHiLo) {
      this.opponentDiceManager?.resetAllOpponents();
    }

    // Enable server-authoritative mode so throw physics gets sent to server
    this.diceController?.setServerAuthoritativeMode(true);
    this.diceController?.handleRollAuthorize();

    // Local thrower fires physics immediately — smooth, no pre-throw RTT lag.
    // (Deferring HI_LO to the server echo was tried as a determinism fix for a
    // phantom tie-break re-roll, but that re-roll appeared with the dice-auth
    // rework, NOT from physics — deferral was already false long before and
    // HI_LO was fine. The re-roll is server-authoritative: the client only ever
    // spawns a re-roll die because the server re-adds us to rollAuthorizedFor.
    // Root cause is being captured via server logs in HiLoController, not
    // papered over with deferral lag here.)
    this.diceController?.setDeferPhysicsOnThrow(false);

    // HI_LO: settled dice stay LIVE sleeping colliders (knockable, fall with
    // gravity) — the server world keeps the twin body at the same pose, so
    // the knock computes identically everywhere. Other phases park settled
    // dice (single-shooter clean-world model).
    this.diceController?.setParkOnStop(!isHiLo);

    // Create dice if we don't have the right number. HI_LO re-rolls (tie
    // break) deliberately keep the existing stopped die at its landed
    // position — "dice stop where they land". Instead of teleporting it
    // back into frame, we put the camera into follow mode so it lerps to
    // center on the die wherever it ended up. Without this, dice that
    // landed outside the default-camera framing were untappable.
    if (this.diceController) {
      const currentDiceCount = this.diceController.getDice().length;
      const isHiLoReroll = isHiLo && currentDiceCount === expectedDiceCount;

      if (isHiLoReroll) {
        this.diceController.pickupAllDice();
        this.cameraController?.setDice(this.diceController.getDice());
        // Follow mode (not 'default') so the camera frames whatever spot
        // the die actually landed on, keeping it centered for pickup.
        this.cameraController?.setMode('follow');
      } else if (isHiLo || currentDiceCount !== expectedDiceCount) {
        const positions = this.getDicePositions(expectedDiceCount);
        this.diceController.createDice(positions);
        this.cameraController?.setDice(this.diceController.getDice());
        this.cameraController?.setMode('default');

        if (isHiLo) {
          this.diceController.pickupAllDice();
        }
      }
    }

    // Ensure we're in shooting phase when roll is authorized
    // Roll states own their own betting-exit via the v2 state machine;
    // no implicit setPhase('shooting') here.

    this.diceController?.setEnabled(true);
  }

  /**
   * Handle old game:state events.
   * With the unified game:update path active, this is now a no-op —
   * the gameStateMachine processes game:update via TaskPool instead.
   * Kept for backward compatibility during migration.
   */
  private handleGameState(_data: GameStateSnapshot): void {
    // No-op: game:update → TaskPool → gameStateMachine handles all state processing.
    // The old deferral logic (deferredGameState, runPostRollSequence, etc.) is removed.
  }

  /**
   * Cross-cutting per-update bookkeeping. Runs BEFORE the state machine
   * processes the update, so each state's onEnter/onUpdate sees a
   * synced world: opponent dice loaded, bet zones in 3D, sidebet
   * listings in the HUD, wallet input gate set, timer ring synced,
   * camera betting-anchor primed.
   *
   * State-specific concerns (camera mode, wallet open/closed, instruction
   * banner copy, practice dice spawn) live in the v2 state classes
   * themselves and run AFTER this — they get the final word. setPhase
   * is called HERE because the betting-anchor setup it owns
   * (cameraController.setBettingAnchor + bettingController.setBettingCenter
   * + bettingHUD3D.setGroundCenter) needs to land BEFORE state classes
   * run enterBettingMode (which lerps the camera to that anchor).
   */
  /**
   * Lightweight per-update scalar bookkeeping — no scene/DOM/bet-zone side
   * effects. Split out of applyPerUpdateBookkeeping so the state:full resume
   * path can run it too WITHOUT the heavier updateBetZones (which would
   * double-apply bills on top of handleStateFull's applyBetZones). These
   * scalars gate dice-swap eligibility, spectator-follow, and camera-zoom —
   * consumers that fire from OTHER socket events (dice:phase, dice:result)
   * which can arrive after a resume but before the next game:update. Leaving
   * them stale was the latent post-resume bug.
   */
  private applyUpdateScalars(update: GameUpdate): void {
    this.lastUpdateAnimated = update.animated !== false;
    this.currentState = update.gameState;
    this.playerCount = update.players?.length ?? 0;
    this.bankerId = update.bankerId ?? null;
    this.bankerHasRolled = update.bankerHasRolled ?? false;
    const cs = update.customState as { point?: number | null } | undefined;
    this.crapsPoint = cs?.point ?? null;
  }

  private applyPerUpdateBookkeeping(update: GameUpdate): void {
    this.applyUpdateScalars(update);

    // Solo grind is single-player → hide the local mic + chat affordances.
    // Driven off gameType every update so it resets to visible on cee-lo / craps.
    this.playerProfilesOverlay?.setSocialEnabled(update.gameType !== 'solo');

    // Solo grind session net ticker — feed the local player's live balance.
    if (update.gameType === 'solo') {
      const localId = this.networkManager.getPlayerId();
      const bal = update.players?.find((p) => p.playerId === localId)?.balance;
      if (typeof bal === 'number') soloStacksCounter.setBalance(bal);
      // Roll ledger rides customState — pure state, so it survives resume.
      const solo = update.customState as { rollHistory?: SoloRollHistoryEntry[] } | undefined;
      soloRollHistoryOverlay.setEntries(solo?.rollHistory ?? []);
    }

    // Reconcile each player's card balance immediately from the authoritative
    // server value, so the pill tracks a bet/payout the moment it lands instead
    // of lagging behind the state's transition animations (the full profile
    // refresh runs inside the state hooks, which can sit behind a camera lerp).
    for (const p of update.players ?? []) {
      if (typeof p.balance === 'number') {
        this.playerProfilesOverlay?.applyServerBalance(p.playerId, p.balance);
      }
    }

    const snapshot = this.gameUpdateToSnapshot(update);
    this.lastSnapshot = snapshot;
    this.updateSideBetListings(snapshot);
    this.updateBetZones(snapshot, { syncStacks: true });
    this.refreshProfileSidebets(snapshot.betZones ?? [], snapshot.gameType, this.lastUpdateAnimated);
    this.updateBettingInputGate();
    this.applyTurnTimer(update);
    this.applyBettingActivityBannerSuppression(update);
    this.syncLocalTableStack(update);

    // Opponent dice rig loading (avatars / equipped dice) — keeps the
    // OpponentDiceManager in sync with the player list. The per-state
    // dice creation / clearing logic that updateDiceLayout USED to do
    // now lives in the v2 state classes themselves.
    if (this.opponentDiceManager) {
      const expectedDiceCount = this.getDiceCountForCurrentState();
      this.loadOpponents(snapshot, expectedDiceCount);
    }
  }


  // HI_LO resolution and post-roll animations are now handled by
  // gameStateMachine state handlers (HiLoStateHandler.onExit, RollStateHandler.onExit)

  /**
   * Mirror the local player's table stack (the bullet) from the server onto the
   * wallet HUD. The server is authoritative — it seeds the stack from the buy-in
   * and moves it on every bet/win — so the HUD shows what's actually spendable
   * at the table, not the full bank. walletBalance is display-only (bets never
   * decrement it locally), so overwriting it each update is safe.
   */
  private syncLocalTableStack(update: GameUpdate): void {
    const localId = this.networkManager.getPlayerId();
    if (!localId) return;
    const me = update.players?.find((p) => p.playerId === localId);
    if (!me || typeof me.balance !== 'number') return;
    this.bettingController?.setBalance(me.balance);
    this.bettingHUD3D?.updateBalance(me.balance);

    // Re-buy: a landed re-buy (carries `rebuy`) confirms + closes the modal;
    // the green count-up already rides the balance change.
    if (me.rebuy && me.rebuy > 0) {
      this.rebuyRequested = false;
      this.rebuyModal?.hide();
    }

    // Out of bullet → re-buy. Auto-fire if the player opted in, else prompt.
    if (me.balance <= 0) {
      if (!this.rebuyRequested) {
        if (this.autoRebuy) {
          this.rebuyRequested = true;
          this.networkManager.sendRebuy(0); // 0 → server applies one bullet
        } else {
          this.showRebuyPrompt();
        }
      }
    } else {
      // Recovered (re-buy or win) — re-arm for the next bust.
      this.rebuyRequested = false;
    }
  }

  private showRebuyPrompt(): void {
    if (!this.rebuyModal) {
      this.rebuyModal = new RebuyModal();
      this.rebuyModal.on('rebuy', ({ auto }) => {
        this.autoRebuy = auto;
        this.rebuyRequested = true;
        this.networkManager.sendRebuy(0); // 0 → server applies one bullet
      });
    }
    this.rebuyModal.show();
  }

  private clearOpponentResetTimer(playerId: string): void {
    const timer = this.opponentResetTimers.get(playerId);
    if (timer) {
      clearTimeout(timer);
      this.opponentResetTimers.delete(playerId);
    }
  }

  /**
   * Handle payout from server — fly cash notes from dice to local player's profile.
   * Only positive amounts (wins) trigger the flyout animation.
   */
  /** Balance update — no-op, balance applied via game:update state machine */
  private handlePayout(_data: { amount: number; newBalance: number; reason?: string }): void {}

  /** Roll wins — no-op, wins processed via game:update state machine */
  private handleRollWins(_data: { wins: Array<{ playerId: string; amount: number; betType: string }> }): void {}

  /**
   * Craps: a one-roll sidebet has resolved against the just-completed dice.
   * The server has already applied the balance change; the client plays a
   * flyout for each non-zero payout so the win visibly lands on the
   * recipient's profile card.
   */
  /** Play the dice result flyout and wait for it to complete */
  private playDiceResultFlyout(
    playerId: string,
    values: number[],
    animated: boolean,
    label: string | null = null,
    labelKind: ResultLabelKind | null = null,
  ): Promise<void> {
    if (!this.playerProfilesOverlay) return Promise.resolve();
    const screenPosition = this.getDiceCenterScreen(playerId);
    const update = new Map<string, DiceResultUpdate | null>();
    update.set(playerId, { values, screenPosition, label, labelKind });
    this.playerProfilesOverlay.updateDiceResults(update, animated);
    if (!animated) return Promise.resolve();
    return this.playerProfilesOverlay.waitForAllFlyouts();
  }

  /**
   * Tie/push flyout — spawn a TIE chip at the shooter's dice center and fly
   * two copies in parallel: one to the banker's profile card, one to the
   * shooter's. Used when the shooter ties the banker's rank (no bust).
   * Resolves once both flyouts settle.
   *
   * Falls back to the banker's dice center if the shooter's dice are no
   * longer in the scene (rare — settlement runs while dice are still
   * visible). If neither dice center resolves, returns immediately so the
   * round flow doesn't stall.
   */
  private async playTieFlyout(bankerId: string, shooterId: string): Promise<void> {
    if (!this.playerProfilesOverlay) return;
    const screenPos = this.getDiceCenterScreen(shooterId)
      ?? this.getDiceCenterScreen(bankerId);
    if (!screenPos) return;
    const TIE_DURATION = 0.9; // seconds — scale-in + hold + arc
    // Tie shares the point/neutral stinger (per the result-sound mapping).
    // Played once here, not inside flyoutTieChip — that runs twice in parallel.
    sound.play(SoundId.DICE_RESULT_POINT);
    await Promise.all([
      this.playerProfilesOverlay.flyoutTieChip(bankerId, screenPos, TIE_DURATION),
      this.playerProfilesOverlay.flyoutTieChip(shooterId, screenPos, TIE_DURATION),
    ]);
  }

  /**
   * Grouped player win flyout: bet icons → money flyout → icons fade out.
   * Icons represent what the player is being paid for.
   * slotIndex distributes multiple players' flyouts around the dice center in a hexagon pattern.
   */
  private playPlayerWinFlyout(
    playerId: string,
    totalAmount: number,
    betTypes: Array<{ type: string; isFade: boolean }>,
    bankerId: string | null,
    slotIndex: number = 0,
    totalSlots: number = 1,
    gameType?: string,
    sfx: boolean = true,
  ): Promise<void> {
    if (!this.playerProfilesOverlay || totalAmount <= 0) return Promise.resolve();

    // Find center spawn position — use the roller's dice, then banker's, then any visible
    let center = this.getDiceCenterScreen(playerId);
    if (!center && bankerId) {
      center = this.getDiceCenterScreen(bankerId);
    }
    if (!center && this.opponentDiceManager) {
      const lastState = this.networkManager.getLastGameState();
      if (lastState?.players) {
        for (const p of lastState.players) {
          center = this.getDiceCenterScreen(p.playerId);
          if (center) break;
        }
      }
    }
    if (!center && this.engine) {
      const canvas = this.engine.canvas;
      center = { x: canvas.clientWidth / 2, y: canvas.clientHeight / 2 };
    }
    if (!center) return Promise.resolve();

    // Stack flyouts horizontally, centered on screen X, keeping dice-center Y.
    const screenPos = this.getHorizontalSlotPosition(center, slotIndex, totalSlots);

    // Get note texture URL
    const noteUrl = getNoteCdnUrl(totalAmount, 'small');
    if (!noteUrl) {
      console.warn('[GameFlow] playPlayerWinFlyout: no note URL for amount', totalAmount);
      return Promise.resolve();
    }

    // Map betTypes to icon URLs
    const isBanker = playerId === bankerId;
    const betIconUrls = betTypes.map(({ type, isFade }) => this.getBetTypeIconUrl(type, isBanker, isFade, gameType)).filter(Boolean) as string[];

    return this.playerProfilesOverlay.flyoutPlayerWin(playerId, totalAmount, betIconUrls, screenPos, noteUrl, sfx);
  }

  /**
   * Horizontally stack flyout spawn points, centered on screen X.
   * All slots share the dice-center Y. Slots are spaced SPACING px apart.
   */
  private getHorizontalSlotPosition(
    center: { x: number; y: number },
    slotIndex: number,
    totalSlots: number,
  ): { x: number; y: number } {
    const SPACING = 140; // pixels between adjacent flyouts
    const canvasWidth = this.engine?.canvas?.clientWidth ?? window.innerWidth;
    const screenCenterX = canvasWidth / 2;
    const count = Math.max(1, totalSlots);
    // Offset for this slot so the group is centered on screenCenterX
    const offset = (slotIndex - (count - 1) / 2) * SPACING;
    return {
      x: screenCenterX + offset,
      y: center.y,
    };
  }

  /**
   * Map a bet type to its icon URL for the win flyout. Uses the shared
   * sidebet-icon registry (sidebetIcon.ts) at the small size variant
   * since the flyout icons render at ~40px.
   *
   *   - 'fade' → routed to bank_wins (banker paid) or bank_loses
   *     (player paid) so the image matches what the recipient rooted
   *     for.
   *   - bank_wins / bank_loses / four_up / three_down / shooter_wins /
   *     shooter_loses / point_4_or_10 / point_5_or_9 / point_6_or_8 /
   *     banker_stake_*: routed directly through the registry.
   */
  private getBetTypeIconUrl(betType: string, isBanker: boolean, isFade = false, gameType?: string): string | null {
    // Faders bet AGAINST the sidebet — show the inverted icon so the image matches what they rooted for.
    const effectiveType = isFade ? this.invertSideBetType(betType) : betType;
    // Game-aware resolution for the generic 'fade' bet (cee-lo banker
    // zone settlements pass 'fade' for both owner and faders; craps
    // banker zone reuses the same string because the shape is identical).
    // Cee-lo → bank_wins/bank_loses; craps → shooter_wins/shooter_loses.
    let resolvedType = effectiveType;
    if (effectiveType === 'fade') {
      const isCraps = gameType === 'craps';
      if (isCraps) {
        resolvedType = isBanker ? 'shooter_wins' : 'shooter_loses';
      } else {
        resolvedType = isBanker ? 'bank_wins' : 'bank_loses';
      }
    } else if (effectiveType === 'banker_win') {
      // Legacy alias from CrapsStrategy.settleTurnInline — server passes
      // 'banker_win' for the shooter's payout. Map to shooter_wins
      // (craps) or bank_wins (cee-lo, defensive).
      resolvedType = gameType === 'craps' ? 'shooter_wins' : 'bank_wins';
    }
    // Win flyout renders the chip large (it flies in over the table, not a
    // cramped profile-card slot) — use the large variant for crisp text.
    const url = getSidebetIconUrl(resolvedType, 'large');
    return url || null;
  }

  private invertSideBetType(betType: string): string {
    switch (betType) {
      case 'bank_wins': return 'bank_loses';
      case 'bank_loses': return 'bank_wins';
      case 'four_up': return 'three_down';
      case 'three_down': return 'four_up';
      case 'shooter_wins': return 'shooter_loses';
      case 'shooter_loses': return 'shooter_wins';
      default: return betType;
    }
  }


  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Arrange Cee-Lo dice into canonical display order:
   *   1-2-3 and 4-5-6 → ascending.
   *   Triples (trips / ceelo) → all equal, natural.
   *   Pair + odd → [pair, pair, point].
   *   Nothing (no scoring combo) → ascending.
   *   Other lengths (e.g. HI_LO single die) → passed through unchanged.
   * Every dice-result consumer (profile icons, live cache, flyout animation)
   * routes through this so server dice order doesn't leak into the UI.
   */
  private static sortCeeloDiceForDisplay(dice: number[]): number[] {
    if (dice.length !== 3) return [...dice];
    const sorted = [...dice].sort((a, b) => a - b);
    // 1-2-3 and 4-5-6 (ascending is already correct).
    if (sorted[0] === 1 && sorted[1] === 2 && sorted[2] === 3) return sorted;
    if (sorted[0] === 4 && sorted[1] === 5 && sorted[2] === 6) return sorted;
    // Triples — all equal after sorting.
    if (sorted[0] === sorted[2]) return sorted;
    // Pair + point. sorted[0]===sorted[1] means the pair is already at the
    // front and the odd die is at the back → [pair, pair, odd] is correct.
    if (sorted[0] === sorted[1]) return sorted;
    // sorted[1]===sorted[2] means pair at the back, odd at the front →
    // swap to [pair, pair, odd].
    if (sorted[1] === sorted[2]) return [sorted[1], sorted[2], sorted[0]];
    // Nothing (no pair, no straight, no triple). Ascending is fine —
    // these rolls are reroll-triggers and usually don't even get displayed.
    return sorted;
  }


  // ============================================================================
  // Composite primitives (called by SDLGame, used by v2 state classes).
  // ============================================================================

  /**
   * Pure choreography: forfeit beat, dice-result flyout, tie flyout,
   * rollWins flyouts in parallel, opponent label cleanup. No
   * decisions about handoff, no dice cleanup — those are owned by
   * the caller (state.onExit) and the destination state's
   * enterBettingMode respectively.
   */
  /**
   * Cee-lo lag-trace marker. Called from main.ts when local dice
   * settle, so we can measure stopped → receive → taskStart → onExit.
   * No-op for craps (instrumentation scope is cee-lo while we
   * baseline; can be widened later).
   */
  markDiceStopped(): void {
    if (this.lastSnapshot?.gameType !== 'ceelo') return;
    this._lagTrace = { stopped: performance.now() };
  }

  private async playRollResolutionPrimitive(update: GameUpdate): Promise<void> {
    if (update.gameType === 'ceelo' && this._lagTrace.stopped !== undefined) {
      this._lagTrace = {};
    }

    if (!update.animated) return;

    if (update.forfeit) {
      this.instructionBanner?.show({ title: 'NO RESULT', text: 'Sidebets Returned' }, true);
      await this.delay(600);
    } else if (update.lastRollResult) {
      this.handleNewRollResult(update.lastRollResult);
    }

    await this.playerProfilesOverlay?.waitForAllFlyouts();

    if (update.tieResult) {
      await this.playTieFlyout(update.tieResult.bankerId, update.tieResult.shooterId);
      await this.delay(400);
    }

    if (update.rollWins && update.rollWins.length > 0) {
      const byPlayer = new Map<string, { total: number; betTypes: Array<{ type: string; isFade: boolean }> }>();
      for (const win of update.rollWins) {
        const entry = byPlayer.get(win.playerId) ?? { total: 0, betTypes: [] };
        entry.total += win.amount;
        entry.betTypes.push({ type: win.betType, isFade: win.isFade ?? false });
        byPlayer.set(win.playerId, entry);
      }
      // One sound owner per settlement: the parallel flyouts each carry the
      // full BET_ICON_POP / CASH_COUNT / CASH_LAND set, so with N winners the
      // sounds stack same-frame into a volume spike. The biggest win owns the
      // audio — its note timeline is the longest, so the single cha-ching
      // lands with the largest visual stack.
      let sfxOwnerId: string | null = null;
      let sfxOwnerTotal = -1;
      for (const [playerId, { total }] of byPlayer) {
        if (total > sfxOwnerTotal) { sfxOwnerId = playerId; sfxOwnerTotal = total; }
      }
      const flyoutPromises: Promise<void>[] = [];
      const totalSlots = byPlayer.size;
      let slotIndex = 0;
      for (const [playerId, { total, betTypes }] of byPlayer) {
        flyoutPromises.push(
          this.playPlayerWinFlyout(playerId, total, betTypes, update.bankerId, slotIndex++, totalSlots, update.gameType, playerId === sfxOwnerId),
        );
      }
      await Promise.all(flyoutPromises);
    }

    if (update.forfeit) {
      this.instructionBanner?.hide();
    }

    this.opponentDiceManager?.hideAllResultLabels();
  }

  /**
   * Pure choreography: shooter handoff (gear-out → equipped-dice
   * announcement → profile refresh → bank-icon flyout). No decision
   * logic — the caller has already established the shooter changed.
   *
   * Bank-icon source captured BEFORE the profile refresh because
   * refreshFromUpdate strips the badge from the previous shooter
   * (GameUpdate carries the NEW bankerId), and reading after returns
   * null.
   */
  private async runShooterHandoffPrimitive(
    update: GameUpdate,
    prevShooterId: string,
    newShooterId: string,
  ): Promise<void> {
    const overlay = this.playerProfilesOverlay;
    if (!overlay) return;

    const bankFromPos = overlay.getBankBadgeScreenPos(prevShooterId) ?? null;
    overlay.hideBankerBadgeFor(prevShooterId);

    // Gear-out of the previous shooter's thumbs runs in parallel with the
    // new shooter's announcement flyout. They affect disjoint elements
    // (animateDiceGearOut excludes newShooterId; the announcement icons
    // are transient overlay <img>s) so there's no contention, and we
    // save ~300ms of sequential dead time before the icons appear.
    await Promise.all([
      overlay.animateDiceGearOut(HiLoAnims.diceGearOutDuration, newShooterId),
      this.playShooterAnnouncement(newShooterId),
    ]);

    // Materialize the new shooter's permanent gear thumbs underneath
    // the just-landed transient announcement icons.
    overlay.refreshFromUpdate(update, {
      localPlayerId: this.networkManager.getPlayerId(),
      localEquippedDice: this.diceController?.getEquippedDice().filter(Boolean) as string[] | undefined,
      getOpponentEquippedDice: (id) => this.opponentDiceManager?.getOpponentEquippedDice(id),
      cdnBase: this.cdnBase,
    });

    if (bankFromPos) {
      await overlay.flyoutBankIcon(newShooterId, bankFromPos, 0.6);
    }
  }


  private buildWalletViewConfig(open: boolean): Parameters<BettingController['setWalletView']>[0] {
    return {
      open,
      openPercent: { x: 0.82, y: 0.84 },
      closedPercent: { x: 0.88, y: 0.96 },
      openAnchor: { left: '30%', top: '0%' },
      closedAnchor: { left: '50%', bottom: '0%' },
      height: 1.7,
      openRotationY: -0.35,
      closedRotationY: -Math.PI,
      openRotationX: 0,
      closedRotationX: (Math.PI * 90) / 180,
      openRotationZ: 0,
      closedRotationZ: (Math.PI*14) / 180,
    };
  }

  private updateBettingInputGate(): void {
    if (!this.bettingController) return;
    if (!this.isBettingState(this.currentState)) return;
    if (!this.currentState || !this.bankerId) {
      return;
    }

    if (this.currentState === CeeloState.PLAYER_BETTING) {
      // Sidebet is open to everyone (including the shooter, who can bet on
      // their own 4+/beat-banker result). Only lock-in disables input.
      const lockedIn = this.bettingHUD3D?.getLockedIn() ?? false;
      const canBet = !lockedIn;
      this.bettingController.setInputEnabled(canBet);
      this.bettingController.setBetZonesVisible(true);
      // Skip wallet positioning while camera is mid-lerp — screenToWorld
      // projections used by setWalletView are inaccurate until the camera
      // settles at the betting anchor. Once settled, the state class's
      // ctx.enterBettingMode → wallet.show() picks up the correct anchor.
      if (this.cameraController?.isBettingSettled() ?? true) {
        this.bettingController.setWalletView(this.buildWalletViewConfig(canBet));
      }
      return;
    }

    const localPlayerId = this.networkManager.getPlayerId();
    const isBanker = localPlayerId === this.bankerId;
    let inputEnabled = true;

    if (this.currentState === CeeloState.BANKER_SET_POT) {
      inputEnabled = isBanker;
      if (!inputEnabled) {
      }
    }

    if (this.currentState === CeeloState.FADING) {
      inputEnabled = !isBanker;
      if (!inputEnabled) {
      }
    }

    this.bettingController.setInputEnabled(inputEnabled);
    const showZonesForAll = this.currentState === CeeloState.BANKER_SET_POT || this.currentState === CeeloState.FADING;
    this.bettingController.setBetZonesVisible(showZonesForAll || inputEnabled);
    // Same gate as PLAYER_BETTING: only set the wallet view once the
    // camera has settled, otherwise screenToWorld is inaccurate.
    if (this.cameraController?.isBettingSettled() ?? true) {
      const open =
        (this.currentState === CeeloState.BANKER_SET_POT && isBanker) ||
        (this.currentState === CeeloState.FADING && !isBanker);
      this.bettingController.setWalletView(this.buildWalletViewConfig(open));
    }
    // Camera set by setBettingAnchor in setPhase - no adjustments here
  }

  getBankerId(): string | null {
    return this.bankerId;
  }

  getPlayerCount(): number {
    return this.playerCount;
  }

  /**
   * Apply server-authoritative reconciliation snapshot to existing dice.
   * Snaps each die to the server's physics state so all clients are in sync
   * before a new die enters the simulation.
   */

  private applyHiLoReconciliation(snapshot: Array<{
    playerId: string;
    position: { x: number; y: number; z: number };
    quaternion: { x: number; y: number; z: number; w: number };
    velocity: { x: number; y: number; z: number };
    angularVelocity: { x: number; y: number; z: number };
    force?: { x: number; y: number; z: number };
    torque?: { x: number; y: number; z: number };
    asleep?: boolean;
  }>): void {
    const localPlayerId = this.networkManager.getPlayerId();
    // Pose-delta thresholds below which an entry is a no-op: under
    // determinism the server and this client agree to float precision, and
    // re-snapping an aligned die every broadcast would just churn the
    // interpolation state (and visibly jitter at-rest dice).
    const POS_EPS_SQ = 1e-4;   // 1 cm
    const QUAT_DOT_EPS = 0.99995;
    for (const entry of snapshot) {
      const dice =
        entry.playerId === localPlayerId
          ? this.diceController?.getDice() ?? []
          : this.opponentDiceManager?.getOpponentActiveDice(entry.playerId) ?? [];
      for (const die of dice) {
        // NEVER touch a held die (frozen mid-drag / awaiting our own echo),
        // and never WAKE a parked mass-0 die — waking a mass-0 body with
        // velocity makes it drift with NO gravity (force = mass·g = 0), the
        // "settled die floats away" bug. Parked dice from version-skewed old
        // servers only ever appear as awake entries, so the mass guard stays.
        if (die.held || die.body.mass === 0) continue;

        const b = die.body;
        const dx = b.position.x - entry.position.x;
        const dy = b.position.y - entry.position.y;
        const dz = b.position.z - entry.position.z;
        const qDot = Math.abs(
          b.quaternion.x * entry.quaternion.x +
          b.quaternion.y * entry.quaternion.y +
          b.quaternion.z * entry.quaternion.z +
          b.quaternion.w * entry.quaternion.w,
        );
        const aligned = dx * dx + dy * dy + dz * dz < POS_EPS_SQ && qDot > QUAT_DOT_EPS;

        if (entry.asleep) {
          // Settled on the server. If we already agree, leave the die alone.
          // If we diverged (e.g. a knock computed differently here), snap the
          // pose and put it back to sleep WITHOUT waking — sleep() zeroes
          // velocities, and the visual correction hides the move.
          if (aligned && this.engine?.physics.isSleeping(b)) continue;
          die.startVisualCorrection(150);
          die.setPosition(entry.position);
          die.setRotationFromQuaternion(entry.quaternion);
          b.force.setZero();
          b.torque.setZero();
          b.sleep();
          continue;
        }

        // Moving on the server. Skip only if fully aligned in pose AND motion.
        if (aligned) {
          const dvx = b.velocity.x - entry.velocity.x;
          const dvy = b.velocity.y - entry.velocity.y;
          const dvz = b.velocity.z - entry.velocity.z;
          if (dvx * dvx + dvy * dvy + dvz * dvz < POS_EPS_SQ) continue;
        }
        // Route through Dice.setPosition / setRotationFromQuaternion so the
        // PhysicsWorld interpolation state (previousPosition / interpolatedPosition)
        // is rewritten — otherwise the next render frame lerps from the stale
        // pre-snap pose to the snapped body.position and the dice visibly slides.
        // Capture the current mesh pose BEFORE snapping the body so the mesh
        // can smoothly lerp from where it was to where physics now is. Physics
        // state snaps immediately (deterministic from this moment), visual
        // slides over ~150ms to hide the teleport.
        die.startVisualCorrection(150);

        die.setPosition(entry.position);
        die.setRotationFromQuaternion(entry.quaternion);
        die.setVelocity(entry.velocity);
        die.setAngularVelocity(entry.angularVelocity);
        // Mirror the server's force/torque accumulators exactly — should be
        // zero in steady-state Cannon (gravity is re-applied at step start, not
        // accumulated), but applying whatever the server sent guarantees parity
        // even if anyone adds custom forces later.
        if (entry.force) {
          die.body.force.set(entry.force.x, entry.force.y, entry.force.z);
        } else {
          die.body.force.setZero();
        }
        if (entry.torque) {
          die.body.torque.set(entry.torque.x, entry.torque.y, entry.torque.z);
        } else {
          die.body.torque.setZero();
        }
        die.body.wakeUp();
      }
    }
  }

  getCurrentState(): string | null {
    return this.currentState;
  }

  isHiLoActive(): boolean {
    return this.currentState === CeeloState.HI_LO;
  }

  /**
   * Update opponent dice (call in game loop)
   */
  update(deltaTime: number): void {
    this.opponentDiceManager?.update(deltaTime);
    // (Removed) The legacy "wait for camera settle, then call enterWallet"
    // block lived here. Wallet entry is now driven by the state class
    // awaiting ctx.enterBettingMode, which awaits camera.enterBetting and
    // then bettingController.showWallet directly.

    // (Banner dice-pickup suppression moved to diceThrowStateHandler —
    // edge-driven off the throw state machine, nothing polled per frame.)

    // No 'betting-transition' suppression — state classes order the banner
    // explicitly via `await ctx.enterBettingMode(...)` then
    // `ctx.showInstruction(...)`. The wallet's idempotent show/hide makes
    // this work for the first betting phase entry (full chain runs) and
    // for sub-state transitions (idempotent no-op).

    // Wallet entry is now driven by state classes via
    // ctx.enterBettingMode → bettingController.showWallet, which awaits the
    // camera lerp internally. The legacy "wait for settle, then call
    // enterWallet" loop block was removed during the async refactor.
  }

  /**
   * Load opponent dice when players are in the game
   * Creates dice once per opponent, then sets active count based on game state
   */
  /** True while either game mode is in its simultaneous-roll HI_LO phase. */
  private isHiLoState(): boolean {
    return this.currentState === CeeloState.HI_LO || this.currentState === CrapsState.HI_LO;
  }

  /**
   * Enforce a single-shooter physics world before a throw replays (Phone Dice
   * loadPlayer model). Parks every die EXCEPT the shooter's — all other
   * opponents AND, when a remote player is shooting, the local player's leftover
   * dice — so only the shooter's dice are live colliders. This makes the throw
   * path self-sufficient: it no longer depends on the previous roller's STOPPED
   * or the game:update-driven betting clear having landed first (the cross-
   * channel race behind "first roller clean, second roller needs a snap").
   *
   * Skipped during HI_LO, where all contenders roll at once and every die must
   * stay live. Idempotent — settled dice are already parked, so re-parking is a
   * no-op, and the shooter's own pickup→release restores full collision.
   */
  private loadShooter(shooterId: string | null): void {
    if (!shooterId || this.isHiLoState()) return;
    this.opponentDiceManager?.parkAllExcept(shooterId);
    if (shooterId !== this.networkManager.getPlayerId()) {
      this.diceController?.parkLeftoverDice();
    }
  }

  /**
   * Guarantee an opponent's dice exist AND are built from their equipped asset
   * before a throw is replayed. Pulls equippedDice from the v2 overlay cache
   * (the source of truth — see PlayerProfilesOverlay.getEquippedDiceFor), so a
   * dice:phase(THROWN) that races ahead of the loadOpponents game:update still
   * builds the correct collider instead of the info-less fallback cube.
   */
  private ensureOpponentLoaded(playerId: string): void {
    const mgr = this.opponentDiceManager;
    if (!mgr) return;
    if (playerId === this.networkManager.getPlayerId()) return;
    if (mgr.hasOpponent(playerId)) return;

    const equippedDice = this.playerProfilesOverlay?.getEquippedDiceFor(playerId) ?? undefined;
    mgr.loadOpponent(playerId, {
      username: 'Player',
      equippedDice,
    });
    if (equippedDice?.length) {
      this.playerEquippedDice.set(playerId, equippedDice);
    }
  }

  private loadOpponents(data: GameStateSnapshot, activeDiceCount: number): void {
    if (!this.opponentDiceManager) return;

    const localPlayerId = this.networkManager.getPlayerId();
    const players = data.players ?? [];

    for (const player of players) {
      // Skip local player
      if (player.playerId === localPlayerId) continue;

      // Load opponent if not already loaded (creates all 3 dice once)
      if (!this.opponentDiceManager.hasOpponent(player.playerId)) {
        this.opponentDiceManager.loadOpponent(player.playerId, {
          username: player.username ?? 'Player',
          avatar: player.avatar,
          equippedDice: player.equippedDice,
        });
        // Cache equipped dice for profile display
        if (player.equippedDice?.length) {
          this.playerEquippedDice.set(player.playerId, player.equippedDice);
        }
      } else if (player.equippedDice?.length) {
        // Already loaded: re-skin the 3D dice if this opponent swapped (their
        // thumbnails already updated off the broadcast; the mesh must follow)
        // OR if any slot is still wearing the fallback red box (asset load
        // failed / die was busy when the preload finished) — retry each
        // update until the real skin lands.
        const current = this.opponentDiceManager.getOpponentEquippedDice(player.playerId);
        if (current.join('|') !== player.equippedDice.join('|')
            || this.opponentDiceManager.hasFallbackSlots(player.playerId)) {
          this.opponentDiceManager.updateEquippedDice(player.playerId, player.equippedDice);
          this.playerEquippedDice.set(player.playerId, player.equippedDice);
        }
      }

      // Set active dice count for current game phase
      this.opponentDiceManager.setActiveDiceCount(player.playerId, activeDiceCount);
    }

    // NOTE: per-update opponent teardown (removeOpponentsExcept) was REMOVED — it
    // ran on every game:update and trusted data.players to be the full roster, but
    // it isn't always, so it tore down active/dragging opponents → dice flicker and
    // "removed instead of falling" when a drag ends. Deliberate-leaver cleanup must
    // happen at a boundary where the full roster is guaranteed (e.g. WAITING), not
    // here. removeOpponentsExcept() is kept on OpponentDiceManager for that future use.
  }


  /**
   * Derive per-player sidebet icons from bet zones and push to the overlay.
   * Owner gets the sidebet icon directly. Faders get the inverted icon (they bet AGAINST).
   *
   * Banker zones (shooter's main stake) also surface here once at least
   * one player has faded — both the shooter and every fader get the
   * shooter-stake chip on their profile. Cee-lo uses a bank glyph,
   * craps uses a dice glyph.
   */
  private refreshProfileSidebets(zones: BetZone[], gameType: 'ceelo' | 'craps' | 'solo', animated: boolean): void {
    if (!this.playerProfilesOverlay) return;
    const sidebetMap = new Map<string, Array<{ type: string; count: number }>>();
    const addForPlayer = (playerId: string, type: string) => {
      const list = sidebetMap.get(playerId) ?? [];
      const entry = list.find(e => e.type === type);
      if (entry) entry.count += 1;
      else list.push({ type, count: 1 });
      sidebetMap.set(playerId, list);
    };
    // Banker / shooter main-stake chips reuse the existing wins/loses
    // bet types: owner is rooting for the bank (or shooter) to win;
    // faders are rooting for it to lose. Cee-lo uses bank_wins/loses
    // (BW/BL); craps uses shooter_wins/loses (SW/SL). Same icon shape,
    // different label per game.
    const stakeOwnerType = gameType === 'craps' ? 'shooter_wins' : 'bank_wins';
    const stakeFaderType = gameType === 'craps' ? 'shooter_loses' : 'bank_loses';
    for (const zone of zones) {
      const fades = zone.fades ?? [];
      if (zone.type === 'banker') {
        if (fades.length === 0) continue;
        // One chip on the shooter (rooting for win), one on each
        // fader (rooting for loss). Clears when the zone is removed
        // at settle().
        addForPlayer(zone.ownerPlayerId, stakeOwnerType);
        for (const fade of fades) addForPlayer(fade.playerId, stakeFaderType);
        continue;
      }
      if (zone.type !== 'sidebet' || !zone.betType) continue;
      // Sidebet icons appear only once someone has faded the sidebet.
      // Once faded: owner gets their own icon (BW/4+), each fader gets the inverted icon (BL/3-).
      if (fades.length === 0) continue;
      addForPlayer(zone.ownerPlayerId, zone.betType);
      for (const fade of fades) {
        addForPlayer(fade.playerId, this.invertSideBetType(zone.betType));
      }
    }
    this.playerProfilesOverlay.updateSidebets(sidebetMap, animated);
  }

  private updateSideBetListings(data: GameStateSnapshot): void {
    const sideBets = data.sideBets ?? [];

    const playerNameById = new Map<string, string>();
    (data.players ?? []).forEach((player) => {
      playerNameById.set(player.playerId, player.username ?? 'Player');
    });

    const labelMap: Record<string, string> = {
      bank_wins: 'BANK WINS',
      bank_loses: 'BANK LOSES',
      four_up: '4+',
      three_down: '3-',
    };

    const listings = sideBets.map((bet, index) => ({
      id: `${bet.playerId}-${index}`,
      playerName: playerNameById.get(bet.playerId) ?? 'Player',
      targetName: bet.targetPlayerId ? playerNameById.get(bet.targetPlayerId) : undefined,
      typeLabel: labelMap[bet.type] ?? bet.type,
      amount: bet.amount,
    }));

    // Update 3D HUD
    this.bettingHUD3D?.setSidebetListings(listings);
  }

  private updateBetZones(data: GameStateSnapshot, options?: { syncStacks?: boolean }): void {
    if (!this.bettingController) return;
    const zones = data.betZones ?? [];
    const playerNames = new Map<string, string>();
    const playerAvatars = new Map<string, string>();
    (data.players ?? []).forEach((player) => {
      playerNames.set(player.playerId, player.username ?? 'Player');
      const avatar = (player as { avatar?: string }).avatar;
      if (avatar) playerAvatars.set(player.playerId, avatar);
    });

    // Betting center + create zone are set ONCE in setPhase (enteringFromNonBetting).
    // No per-update repositioning — the anchor raycast result doesn't change.
    this.bettingController.setBettingCenterAnchorTracking(null);

    // Per-zone transition detection from the previous snapshot. Two
    // cases the legacy bet_zone_updated event used to handle, now
    // folded in here so the unified game:update.betZones flow stays
    // the single source of truth:
    //   1. visible:false → visible:true — queued sidebet just
    //      surfaced. Drop the creator's parked bills (if any), or
    //      build bills for the existing total (spectator path).
    //   2. New zone with a pending create-ack — ack arrived before
    //      this game:update; drain the ack now that the zone exists.
    const surfaceTargets: BetZone[] = [];
    const ackDrains: Array<{ zone: BetZone; requestId: string }> = [];
    for (const zone of zones) {
      const prev = this.prevBetZonesById.get(zone.id);
      if (prev && prev.visible === false && zone.visible !== false) {
        surfaceTargets.push(zone);
      }
      const pendingReqId = this.pendingCreateAcks.get(zone.id);
      if (pendingReqId) {
        ackDrains.push({ zone, requestId: pendingReqId });
        this.pendingCreateAcks.delete(zone.id);
      }
    }

    this.bettingController.setBetZones(zones, playerNames, playerAvatars);

    // Run surface + ack-drain BEFORE syncBetZoneStacks: these place the local
    // player's own bills (a freshly-created zone's owner stack, a surfaced
    // queued sidebet) into the real zone. syncBetZoneStacks then diffs the
    // server's per-contributor denominations against the bills already placed,
    // so those local notes are seen as present and are NOT re-dropped. The
    // BetZoneManager already has the new zone registered (setBetZones above),
    // so bill placement can find it.
    for (const zone of surfaceTargets) {
      this.bettingController.handleBetZoneSurfaced(zone);
    }
    for (const { zone, requestId } of ackDrains) {
      this.bettingController.handleBetZoneCreated(zone, requestId);
    }

    if (options?.syncStacks) {
      // SINGLE SOURCE OF TRUTH: bill stacks are derived purely from
      // game:update.betZones (owner + per-fade denominations). syncBetZoneStacks
      // diffs the server notes against the bills currently placed in each zone
      // and animates only the newly-added drops (opponents' notes). Animate when
      // this is a real-time update so opponent bills DROP from above; state:full
      // / late-join pass animated:false → instant rebuild (no drop mid-loading).
      this.bettingController.syncBetZoneStacks(zones, this.lastUpdateAnimated);
    }

    // Snapshot the current betZones for next-call diffing.
    this.prevBetZonesById.clear();
    for (const zone of zones) {
      this.prevBetZonesById.set(zone.id, zone);
    }

    // States where players can drop bills into the create-zone (sidebet
    // placement). Includes both cee-lo's PLAYER_BETTING/PLAYER_ROLL and the
    // craps SIDEBET_BETTING phase. Cee-lo's PLAYER_ROLL was kept here
    // historically because sidebets remain droppable while the shooter rolls.
    const sidebetStates: string[] = [
      CeeloState.PLAYER_BETTING,
      CeeloState.PLAYER_ROLL,
      CrapsState.SIDEBET_BETTING,
      // Solo: the player's selection bet IS a typed (sidebet-style) zone —
      // dropping into the create-zone opens the LO/SEVEN/HI (or face) picker.
      SoloState.BETTING,
    ];
    // Server caps visible sidebets at MAX_VISIBLE_SIDEBETS (5) — beyond that
    // the dolly stretches the wallet/HUD layout. Keep the zone visible (so
    // the player retains spatial context) but lock it: lock-icon overlay +
    // PLEASE WAIT, drops blocked. Lifts as soon as a zone resolves.
    const MAX_VISIBLE_SIDEBETS = 5;
    const visibleSidebetCount = (zones ?? []).filter(
      (z) => z.type === 'sidebet' && z.status !== 'locked'
    ).length;
    const atSidebetCap = visibleSidebetCount >= MAX_VISIBLE_SIDEBETS;
    const createEnabled = (data.gameType === 'ceelo' || data.gameType === 'craps' || data.gameType === 'solo')
      && sidebetStates.includes(data.state);
    this.bettingController.setCreateZoneEnabled(createEnabled);
    this.bettingController.setCreateZoneLocked(createEnabled && atSidebetCap);
    // Solo: keep the drop-zone at its reachable offset, but land the PLACED bet
    // dead-center (banker index-0) rather than a sidebet-ring slot.
    this.bettingController.setCreateZoneCentered(false);
    this.bettingController.setSoloCentered(data.gameType === 'solo');
    const localPlayerId = this.networkManager.getPlayerId();
    const isBanker = localPlayerId && localPlayerId === data.bankerId;
    // BANKER_SET_POT (cee-lo) and SHOOTER_SET_STACK (craps) are the same
    // mechanic — banker/shooter creates the main zone. Same flag for both.
    const isBankerPotPhase =
      ((data.gameType === 'ceelo' && data.state === CeeloState.BANKER_SET_POT)
        || (data.gameType === 'craps' && data.state === CrapsState.SHOOTER_SET_STACK))
      && !!isBanker;
    this.bettingController.setBettingPhaseFlags(isBankerPotPhase, createEnabled);

    // Unified betting-phase camera framing. BANKER_SET_POT/SHOOTER_SET_STACK,
    // FADING (same string for both games), and PLAYER_BETTING/SIDEBET_BETTING
    // all run the same bounds-fit so the camera distance stays consistent
    // across the betting flow — no surprise zoom when the banker finishes
    // setting the pot or when sidebetting opens. The math (Math.max(base,
    // required)) keeps the editor anchor as the floor: with just the banker
    // zone + create zone in view the camera sits at the anchor; only ring-1
    // and ring-2 sidebets push past it.
    const bettingPhases: string[] = [
      CeeloState.BANKER_SET_POT,
      CeeloState.FADING,
      CeeloState.PLAYER_BETTING,
      CrapsState.SHOOTER_SET_STACK,
      CrapsState.SIDEBET_BETTING,
      // CrapsState.FADING is the same string ('fading') as CeeloState.FADING,
      // already covered.
      SoloState.BETTING,
      SoloState.FADE,
    ];
    if (bettingPhases.includes(this.currentState ?? '')) {
      const visibleCount = (zones ?? []).filter(
        (z) => z.type === 'sidebet' && z.visible !== false && z.status !== 'locked'
      ).length;
      if (visibleCount !== this.lastSidebetCount) {
        this.lastSidebetCount = visibleCount;
        const bounds = this.bettingController.getBetZoneBounds();
        if (bounds) {
          this.cameraController?.setBettingBoundsKeepLookAt(bounds, {
            maxExtraDolly: Infinity,
          });
          this.cameraController?.tweenToBettingTarget(CameraAnims.bettingBoundsTweenDuration);
        }
      }
    } else {
      this.lastSidebetCount = null;
    }
  }

  /**
   * Compute the betting zone center from the editor's betting camera anchor.
   * Uses the actual raycast ground hit (camera → lookAt → first mesh intersection)
   * stored by GameScene.raycastBettingAnchorGround(). Falls back to lookAt XZ
   * if no ground hit was computed.
   */
  private computeBettingZoneCenter(
    anchor: THREE.Object3D | undefined,
    anchorLookAt: number[] | undefined,
  ): { x: number; y: number; z: number } {
    const DEFAULT_CENTER = { x: 0, y: 0.01, z: -0.8 };

    if (!anchor || !anchorLookAt) {
      return DEFAULT_CENTER;
    }

    // Prefer the actual ground hit point from the betting anchor raycast.
    // This is where the camera ray actually intersects geometry, which differs
    // from lookAt XZ when the lookAt target is below the ground plane.
    const groundHit = anchor.userData?.groundHit as number[] | undefined;
    if (groundHit) {
      return { x: groundHit[0], y: groundHit[1], z: groundHit[2] };
    }

    // Fallback: project lookAt XZ onto ground
    return { x: anchorLookAt[0], y: 0.01, z: anchorLookAt[2] };
  }

  private applyBetZones(zones: BetZone[], players: Array<{ playerId: string; username?: string; avatar?: string }>): void {
    if (!this.bettingController) return;
    const playerNames = new Map<string, string>();
    const playerAvatars = new Map<string, string>();
    players.forEach((player) => {
      playerNames.set(player.playerId, player.username ?? 'Player');
      if (player.avatar) playerAvatars.set(player.playerId, player.avatar);
    });
    this.bettingController.setBetZones(zones, playerNames, playerAvatars);
  }

  /**
   * Targeted ack from the server: a bet zone the local player just
   * created (matched by `requestId`) is now live. Triggers the
   * bill-transfer animation from the create zone into the new
   * sidebet zone. Spectators don't see this event — they pick up the
   * new zone from `game:update.betZones` via updateBetZones.
   *
   * Race tolerance: if the ack arrives before the corresponding
   * game:update.betZones (Socket.IO doesn't guarantee strict
   * cross-event delivery order), we park it in pendingCreateAcks and
   * drain on the next updateBetZones call.
   */
  private handleBetZoneCreateAck(data: { requestId: string; zoneId: string }): void {
    const zone = this.lastSnapshot?.betZones?.find((z) => z.id === data.zoneId);
    if (zone) {
      this.bettingController?.handleBetZoneCreated(zone, data.requestId);
      return;
    }
    this.pendingCreateAcks.set(data.zoneId, data.requestId);
  }

  private handleBetZoneFadeResult(data: { requestId: string; zoneId: string; acceptedAmount: number; rejectedAmount: number }): void {
    this.bettingController?.handleBetZoneFadeResult(data);
  }

  private handleBetZoneCreateRejected(data: { requestId: string; reason: string; maxAmount?: number }): void {
    this.bettingController?.handleBetZoneCreateRejected(data.requestId);
    // Server refused the create; bills returned to the wallet. Drop the
    // in-flight commit deduction (no server balance change will clear it).
    const localId = this.networkManager.getPlayerId();
    if (localId) this.playerProfilesOverlay?.releaseLocalInFlight(localId);
    // Table-limit bounce: tell the player why their bills came back and
    // what the cap for this bet actually is.
    if (data.reason === 'over_max_bet' && data.maxAmount) {
      showToast(`Table max for this bet is ${formatDenomination(data.maxAmount)}`, { type: 'info' });
    }
  }
}
